import * as THREE from 'three';
import {
  BASIN_R,
  CIST_STAIR_HALF,
  GATE_R,
  ISLAND_R,
  KEEP_HALF,
  KEEP_S,
  RIM_R,
  STAIR_AT,
  T_PATH,
  cisternLayout,
  sevWet,
  yardLayout,
  type BattleState,
  type MapDef,
  type SeventhRules,
  type SeventhState,
} from '@somnhold/sim';
import { Kit, place } from '../seasons/kit.ts';
import type { OmenCtx } from '../omens/common.ts';
import { SCALE, WORLD_PER_HEX } from './coords.ts';
import { GlowLayer } from './glow.ts';
import { sendaModel } from '../models/sendas.ts';
import { SkyDome } from './snow.ts';
import type { TerrainBuild } from './terrain.ts';

/**
 * The seventh altar (docs/sendas/paladin.md, chapter 5), for the paladin's path: Somnhold itself, at night, then under
 * it. Never chapter 4's wet green gorge nor the salt's white: here it is the bastion's own dark stone under the Order's
 * torches, and below it a cistern of pale stone far older than the fortress, cold, still, lit only by its fires and a
 * shaft of moonlight down the old well onto the island.
 *
 * Places (sim battle/seventh-layout.ts; each place keeps its own hour, the scenes' skies agree):
 * - `keepNight` (act 1, the opening) and `keepFar` (the chapter's cinematic: the same board, the camera low and far): the
 *   bastion's yard at night, the keep's block to the north (its sim floor exactly: y ≤ KEEP_S, |x| ≤ KEEP_HALF) drawn as a
 *   tall tower of dark stone with lit slits, crenels and corner turrets; the curtain wall, the barracks, the Order's
 *   torches round the yard (glows); six heaps of the fall's rubble over the stair's mouth, each shrinking as it is cleared
 *   (one merged mesh, one uniform each); the four old drains; and once the last heap falls, the stair revealed, older than
 *   the tower, an eye carved on every step.
 * - `cistern7` (acts 3 and 4, the campfire, the speech, the close): the round basin (its floor a shallow cone from the
 *   island's foot to its lip, so the water's marks are rings), the island with the seventh altar and its great eye (shut
 *   until the close) and seven small eyes round its curb that light as the oath fills, the dry rim, the four old gates on
 *   the diagonals (their wheels turn while they run), their channels, the eight salt runnels (white when salted), the
 *   stair down at the south, the outer wall's arcade; the water one level in act 3, one per quarter in act 4 (one mesh);
 *   ripples and prints where La Que Huyó walks unseen; her lines on the floor while she marks them.
 * - `keepTop` (the epilogue): the top of the keep in the morning sun, the yard far below with the salt folk and the
 *   knights at their bread and the monastery's dam wheel, the Way going down out of the gate into the hills.
 * - `roots` (act 2, a crypt): RootsCrypt below, the descent's rooms re-dressed.
 *
 * Draw calls (cistern, the heaviest): the floor (one), the masonry (one), the lit pieces (one), the water (one), the
 * runnels (one), the channels' water (one), the wheels and the leaves (one instanced each), the eye (two, tiny), the
 * shaft (one), the decals (one instanced), the lines (two, only while marked), the glows (one). No lights of its own:
 * the board's are repainted (HOURS); fires, eyes and the altar glow. Nothing allocated per frame.
 */

const S = WORLD_PER_HEX;
const K = S / 1000;
const RED = new THREE.Color('#ff4f3a');
const RED_SOFT = new THREE.Color('#ff8a6e');
const WHITE = new THREE.Color('#ffffff');
const COLD = new THREE.Color('#dfe8ff');
const FIRE = new THREE.Color('#ff9a52');
const FIRE_CORE = new THREE.Color('#ffd2b0');

export const SEVENTH_PLACES = new Set(['keepNight', 'keepFar', 'cistern7', 'keepTop']);
type Place = 'keepNight' | 'keepFar' | 'cistern7' | 'keepTop';

const HOURS: Record<Place, { bg: string; fog: string; near: number; far: number; hemi: [string, string, number]; key: [string, number]; rim: [string, number]; sky: [string, string, string] | null; sun: [number, number, number] }> = {
  // Night in the yard: the moon over the keep's shoulder, cold blue, the torches the warmth.
  keepNight: { bg: '#070a12', fog: '#0b1019', near: 30, far: 95, hemi: ['#5c6c94', '#06070a', 0.95], key: ['#a3b6e4', 0.72], rim: ['#6f8cf2', 1.05], sky: ['#02040a', '#0b1222', '#4a5a80'], sun: [0.35, 0.55, -0.76] },
  keepFar: { bg: '#070a12', fog: '#0b1019', near: 40, far: 140, hemi: ['#5c6c94', '#06070a', 0.95], key: ['#a3b6e4', 0.72], rim: ['#6f8cf2', 1.05], sky: ['#02040a', '#0b1222', '#4a5a80'], sun: [0.35, 0.55, -0.76] },
  // Under the keep: no sky, a cold dark, the moon's shaft down the old well.
  cistern7: { bg: '#04060b', fog: '#070b13', near: 22, far: 70, hemi: ['#56668e', '#040508', 0.95], key: ['#b0c4ee', 0.62], rim: ['#5c7ce0', 0.95], sky: null, sun: [0.1, 1, 0.05] },
  // The morning after: a clear cold sun low in the east, the hills blue, the stone pale.
  keepTop: { bg: '#9fb2c8', fog: '#aab9ca', near: 60, far: 230, hemi: ['#c8d4e6', '#262a32', 1.1], key: ['#fff1ec', 1.55], rim: ['#a8c4f0', 0.95], sky: ['#4f78a8', '#c4d2e2', '#ffeae4'], sun: [0.72, 0.3, -0.62] },
};

/* ---------- the cistern in world units ---------- */

const IR = ISLAND_R * K;
const BR = BASIN_R * K;
const RR = RIM_R * K;
const GR = GATE_R * K;
const SH = CIST_STAIR_HALF * K;
/** The basin's floor at the island's foot, a mark's rise, the lip, the island's top (bare at four marks or fewer). */
const F0 = -1.7;
const MARK = 0.14;
const LIP = F0 + 5 * MARK;
const ISLE_Y = F0 + 4 * MARK + 0.07;
/** The axes' sills (the quarters' old dividers): how high over the cone, how wide. */
const SILL = 0.3;
const SILL_W = 0.32;
const BANK = 0.45;

/** The water's height at mark L (to five over the cone; above it, up the basin's bank). */
export const markY = (L: number): number => (L <= 5 ? F0 + Math.max(0, L) * MARK : LIP + (L - 5) * 0.15);
const coneY = (r: number): number => F0 + ((Math.min(BR, Math.max(IR, r)) - IR) / (BR - IR)) * 5 * MARK;
const smooth = (a: number, b: number, x: number): number => {
  const t = Math.max(0, Math.min(1, (x - a) / (b - a)));
  return t * t * (3 - 2 * t);
};

/** The cistern's ground where the pieces stand: the island, the cone, the sills, the bank, the rim, the stair up south. */
const cistGround = (x: number, z: number): number => {
  const r = Math.hypot(x, z);
  if (r < IR - 0.12) return ISLE_Y;
  if (r < IR + 0.18) return ISLE_Y + (coneY(r) - ISLE_Y) * smooth(IR - 0.12, IR + 0.18, r);
  if (r < BR) {
    const sill = r > IR + 0.18 && r < BR - BANK && (Math.abs(x) < SILL_W || Math.abs(z) < SILL_W) ? SILL : 0;
    if (r < BR - BANK) return coneY(r) + sill;
    return coneY(r) * (1 - smooth(BR - BANK, BR, r));
  }
  if (z > 0 && Math.abs(x) < SH + 0.15 && r > RR - 0.3) return Math.max(0, (z - (RR - 0.3)) * 0.62);
  return 0;
};

/* ---------- the yard in world units ---------- */

const KS = KEEP_S * K;
const KH = KEEP_HALF * K;
const KN = KS - 7.8;
const KEEP_TOP = 13.5;
const ST = { x: STAIR_AT.x * K, z: STAIR_AT.y * K, half: 0.95, z0: KS - 0.05, z1: STAIR_AT.y * K + 0.75 };
const WALL_R = 21;
/** The epilogue: the top of the keep at 0, the yard this far below. */
const TOP_DROP = 9.4;

/* ---------- shaders ---------- */

const GLSL_N = /* glsl */ `
float svH(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
float svN(vec2 p) { vec2 i = floor(p), f = fract(p); vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(mix(svH(i), svH(i + vec2(1, 0)), u.x), mix(svH(i + vec2(0, 1)), svH(i + vec2(1, 1)), u.x), u.y); }
float svF(vec2 p) { return svN(p) * 0.5 + svN(p * 2.07 + 5.3) * 0.3 + svN(p * 4.3 + 1.7) * 0.2; }
`;

const GLSL_CONE = /* glsl */ `
float svCone(float r) { return ${F0.toFixed(3)} + clamp((r - ${IR.toFixed(4)}) / ${(BR - IR).toFixed(4)}, 0.0, 1.0) * ${(5 * MARK).toFixed(3)}; }
`;

type StoneU = { uTime: THREE.IUniform<number> };

/**
 * The masonry, one material: `aMason` 1 cuts ashlar courses in what stands (slabs on the tops), 2 the old cistern's great
 * blocks (tall courses, worn edges, pale), 0 leaves the piece its own colour. With `pile`, the rubble: each piece of a
 * heap shrinks into its heap's foot as the heap is cleared (`uProg`, one per heap).
 */
const stoneMat = (u: StoneU, pile = false, prog?: THREE.IUniform<number[]>): THREE.MeshStandardMaterial => {
  const m = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.86, metalness: 0.03, flatShading: true });
  m.onBeforeCompile = (shader) => {
    Object.assign(shader.uniforms, u);
    if (pile && prog) shader.uniforms.uProg = prog;
    shader.vertexShader = shader.vertexShader
      .replace(
        '#include <common>',
        `#include <common>\nattribute float aMason;\nvarying float vMason;\nvarying vec3 vSvW;\nvarying vec3 vSvN;${pile ? '\nattribute float aPile;\nattribute float aOrd;\nattribute float aOx;\nattribute float aOy;\nattribute float aOz;\nuniform float uProg[6];' : ''}`,
      )
      .replace(
        '#include <begin_vertex>',
        `#include <begin_vertex>
${
  pile
    ? `if (aPile > -0.5) {
  float pr = clamp(uProg[int(aPile + 0.5)], 0.0, 1.0);
  // The heap is carried off a stone at a time, the top first (aOrd: when in the clearing each one goes, shrinking
  // into its own middle): what is left keeps its shape and settles a little; cleared, nothing is left. (Never the whole
  // heap squashed: that read as a flat stain on the flags.)
  float gone = pr >= 0.999 ? 1.0 : smoothstep(aOrd, aOrd + 0.1, pr);
  vec3 o = vec3(aOx, aOy, aOz);
  transformed = o + (transformed - o) * (1.0 - gone);
  transformed.y -= pr * 0.08;
}`
    : ''
}
vMason = aMason;
vSvW = (modelMatrix * vec4(transformed, 1.0)).xyz;
vSvN = normalize(mat3(modelMatrix) * objectNormal);`,
      );
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', `#include <common>\nuniform float uTime;\nvarying float vMason;\nvarying vec3 vSvW;\nvarying vec3 vSvN;\n${GLSL_N}`)
      .replace(
        '#include <color_fragment>',
        `#include <color_fragment>
{
  vec3 w = vSvW;
  vec3 nn = normalize(vSvN);
  float up = nn.y;
  float side = 1.0 - smoothstep(0.35, 0.7, abs(up));
  if (vMason > 0.5) {
    bool old = vMason > 1.5;
    float along = abs(nn.x) > abs(nn.z) ? w.z : w.x;
    float ch = old ? 0.46 : 0.24;
    float row = floor(w.y / ch);
    float u = along / ((old ? 0.9 : 0.42) + (old ? 0.5 : 0.2) * svH(vec2(row, 3.0))) + row * 0.5;
    float fv = fract(w.y / ch);
    float fu = fract(u);
    float wj = old ? 0.035 : 0.06;
    float jt = smoothstep(0.0, wj, min(fv, 1.0 - fv)) * smoothstep(0.0, wj * 0.7, min(fu, 1.0 - fu));
    float shade = 0.8 + 0.26 * svH(vec2(floor(u), row));
    shade *= 0.86 + 0.24 * svF(w.xz * 0.7 + w.y * 0.9);
    vec3 c = diffuseColor.rgb * shade;
    if (up > 0.7) {
      vec2 q = w.xz * (old ? 1.3 : 2.4);
      q.x += floor(q.y) * 0.5;
      vec2 f = fract(q);
      jt = smoothstep(0.0, 0.07, min(min(f.x, 1.0 - f.x), min(f.y, 1.0 - f.y)));
      c = diffuseColor.rgb * (0.82 + 0.18 * svH(floor(q))) * (0.85 + 0.22 * svF(w.xz * 1.3));
    }
    c *= mix(0.55, 1.0, jt * side + (1.0 - side) * mix(0.72, 1.0, jt));
    // Old stone: worn hollows, a pale bloom of lime where the water stood.
    if (old) {
      c *= 0.9 + 0.18 * svF(w.xz * 2.3 + w.y * 1.7);
      c = mix(c, vec3(0.72, 0.75, 0.78), smoothstep(0.62, 0.85, svF(vec2(along * 0.8, w.y * 3.0))) * 0.25 * side);
    }
    diffuseColor.rgb = c;
  } else {
    diffuseColor.rgb *= 0.9 + 0.16 * svN(w.xz * 4.0 + w.y * 2.0);
  }
}`,
      );
  };
  m.customProgramCacheKey = () => (pile ? 'somnhold-sev-pile' : 'somnhold-sev-stone');
  return m;
};

type FloorU = { uTime: THREE.IUniform<number>; uStair: THREE.IUniform<number> };

/**
 * The floors. The yard: worn flags of the bastion's dark stone, cracked, a scatter of grit near the keep's foot, packed
 * earth and cobbles beyond the flags, the stair's mouth cut out once open. The cistern: the island's ringed flags, the
 * basin's old slabs dark and wet with a pale tide line at each mark, the bank's steps, the rim's radial flags and the
 * channels' cuts on the diagonals. The keep's top: plain flags (the stone's own slabs).
 */
const floorMat = (u: FloorU, kind: 'yard' | 'cistern'): THREE.MeshStandardMaterial => {
  const m = new THREE.MeshStandardMaterial({ color: '#ffffff', roughness: 0.82, metalness: 0.02 });
  m.onBeforeCompile = (shader) => {
    Object.assign(shader.uniforms, u);
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nvarying vec3 vSvW;')
      .replace('#include <begin_vertex>', '#include <begin_vertex>\nvSvW = (modelMatrix * vec4(transformed, 1.0)).xyz;');
    const yard = /* glsl */ `
  vec2 p = vSvW.xz;
  float r = length(p);
  // (The stair's mouth, open: no floor over it.)
  if (uStair > 0.5 && abs(p.x - ${ST.x.toFixed(3)}) < ${ST.half.toFixed(3)} && p.y > ${ST.z0.toFixed(3)} && p.y < ${ST.z1.toFixed(3)}) discard;
  vec2 q = p * vec2(0.82, 1.02);
  q.x += floor(q.y) * 0.41;
  vec2 cell = floor(q);
  vec2 f = fract(q);
  float joint = smoothstep(0.0, 0.05, min(min(f.x, 1.0 - f.x), min(f.y, 1.0 - f.y)));
  float n = svF(p * 1.2);
  vec3 c = mix(vec3(0.3, 0.32, 0.36), vec3(0.42, 0.44, 0.48), svH(cell)) * (0.84 + 0.26 * n);
  // Cracked flags, a few sunk darker.
  float crack = smoothstep(0.012, 0.0, abs(svN(p * 2.3 + cell * 7.0) - 0.5)) * step(0.82, svH(cell + 3.1));
  c *= 1.0 - crack * 0.45;
  c *= mix(0.78, 1.0, step(0.12, svH(cell + 9.7)));
  c = mix(vec3(0.1, 0.11, 0.12), c, 0.35 + 0.65 * joint);
  // Beyond the flags: packed earth and old cobbles.
  float out_ = smoothstep(14.0, 16.5, r + (svN(p * 0.4) - 0.5) * 3.0);
  vec2 cq = p * 3.2;
  cq.x += floor(cq.y) * 0.5;
  vec2 cf = fract(cq);
  float cj = smoothstep(0.0, 0.16, min(min(cf.x, 1.0 - cf.x), min(cf.y, 1.0 - cf.y)));
  vec3 cob = mix(vec3(0.2, 0.21, 0.22), vec3(0.3, 0.3, 0.31), svH(floor(cq))) * (0.6 + 0.4 * cj);
  vec3 earth = vec3(0.17, 0.165, 0.16) * (0.8 + 0.4 * svF(p * 0.7));
  c = mix(c, mix(earth, cob, smoothstep(0.35, 0.6, svF(p * 0.3 + 4.0))), out_);
  // The fall's grit and dust at the keep's foot.
  float grit = (1.0 - smoothstep(0.0, 4.0, p.y - ${KS.toFixed(3)})) * (1.0 - smoothstep(${(KH + 0.5).toFixed(3)}, ${(KH + 2.5).toFixed(3)}, abs(p.x)));
  c = mix(c, vec3(0.24, 0.24, 0.25) * (0.7 + 0.6 * svH(floor(p * 18.0))), grit * 0.55 * smoothstep(0.3, 0.7, svN(p * 5.0)));
  diffuseColor.rgb = c * 0.9;
  svWet = 0.25 * smoothstep(0.6, 0.8, svF(p * 0.5 + 2.0));`;
    const cistern = /* glsl */ `
  vec2 p = vSvW.xz;
  float r = length(p);
  float a = atan(p.y, p.x);
  float n = svF(p * 1.4);
  vec3 c;
  if (r < ${(IR - 0.12).toFixed(4)}) {
    // The island: ringed flags round the altar, pale and dry.
    vec2 q = vec2(a * (2.0 + floor(r * 1.6) * 2.0), r * 1.6);
    vec2 f = fract(q);
    float jt = smoothstep(0.0, 0.06, min(min(f.x, 1.0 - f.x), min(f.y, 1.0 - f.y)));
    c = mix(vec3(0.5, 0.52, 0.55), vec3(0.6, 0.62, 0.64), svH(floor(q))) * (0.86 + 0.2 * n) * (0.6 + 0.4 * jt);
    svWet = 0.1;
  } else if (r < ${(BR - BANK).toFixed(4)}) {
    // The basin: great old slabs in rings, dark and wet, silt in the joints; a pale tide line at every mark.
    vec2 q = vec2(a * (5.0 + floor(r * 0.9) * 2.5), r * 0.9);
    q.x += floor(q.y) * 0.5;
    vec2 f = fract(q);
    float jt = smoothstep(0.0, 0.05, min(min(f.x, 1.0 - f.x), min(f.y, 1.0 - f.y)));
    c = mix(vec3(0.22, 0.24, 0.27), vec3(0.32, 0.34, 0.37), svH(floor(q))) * (0.78 + 0.34 * n);
    c = mix(vec3(0.07, 0.08, 0.08), c, 0.4 + 0.6 * jt);
    float m5 = (r - ${IR.toFixed(4)}) / ${((BR - IR) / 5).toFixed(4)};
    float tide = 1.0 - smoothstep(0.0, 0.06, abs(fract(m5 + 0.5) - 0.5));
    c = mix(c, vec3(0.6, 0.64, 0.68), tide * 0.45 * step(0.5, m5) * (0.7 + 0.3 * svN(vec2(a * 30.0, r))));
    // The island's foot: a ring of dark weed.
    c *= 0.75 + 0.25 * smoothstep(${IR.toFixed(4)}, ${(IR + 0.6).toFixed(4)}, r);
    // The sills on the axes: dressed stone, lighter.
    float sill = 1.0 - smoothstep(${(SILL_W - 0.04).toFixed(3)}, ${(SILL_W + 0.02).toFixed(3)}, min(abs(p.x), abs(p.y)));
    c = mix(c, vec3(0.42, 0.44, 0.47) * (0.85 + 0.2 * n), sill);
    svWet = 0.85;
  } else if (r < ${BR.toFixed(4)}) {
    // The bank: five worn steps up to the rim, the upper marks cut in it.
    float k = (r - ${(BR - BANK).toFixed(4)}) / ${BANK.toFixed(3)};
    float st = fract(k * 5.0);
    c = vec3(0.36, 0.38, 0.41) * (0.85 + 0.25 * n) * mix(0.6, 1.0, smoothstep(0.0, 0.25, st));
    svWet = 0.6;
  } else {
    // The rim: radial flags of the pale old stone, the channels cut along the diagonals.
    vec2 q = vec2(a * 24.0 / 6.2832 * floor(1.0 + r * 0.35) * 2.0, r * 1.1);
    vec2 f = fract(q);
    float jt = smoothstep(0.0, 0.05, min(min(f.x, 1.0 - f.x), min(f.y, 1.0 - f.y)));
    c = mix(vec3(0.44, 0.46, 0.49), vec3(0.54, 0.56, 0.58), svH(floor(q))) * (0.84 + 0.24 * n) * (0.6 + 0.4 * jt);
    vec2 d = abs(p);
    float chan = 1.0 - smoothstep(0.24, 0.3, abs(d.x - d.y) * 0.7071);
    c = mix(c, vec3(0.08, 0.09, 0.1), chan * step(${BR.toFixed(4)}, r));
    // Lime under the outer wall's foot.
    c = mix(c, vec3(0.62, 0.65, 0.68), smoothstep(${(RR - 0.8).toFixed(3)}, ${RR.toFixed(3)}, r) * 0.25);
    svWet = 0.15 + chan * 0.6;
  }
  diffuseColor.rgb = c * 0.92;`;
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', `#include <common>\nuniform float uTime;\nuniform float uStair;\nvarying vec3 vSvW;\n${GLSL_N}`)
      .replace('#include <color_fragment>', `#include <color_fragment>\nfloat svWet = 0.0;\n{\n${kind === 'yard' ? yard : cistern}\n}`)
      .replace('#include <roughnessmap_fragment>', '#include <roughnessmap_fragment>\nroughnessFactor = mix(roughnessFactor, 0.2, clamp(svWet, 0.0, 1.0));');
  };
  m.customProgramCacheKey = () => `somnhold-sev-floor-${kind}`;
  return m;
};

/**
 * The cistern's water: one mesh, four quarters, each at its own level (`uLevel`, the quarter by `aQ`). It has to read
 * from the game's camera on a phone, so it is lit by itself, cold, never by the board's fires: a slate-blue body
 * (darker where deep, clearer over the shallows), the moon's shaft a pale disc round the island, slow cold ripples
 * running out from the island with glints on them, a bright line of foam wherever it meets the stone (so a ring at
 * three marks shows both its edges), and the drowned island's curb a pale ring under it. `uStir` (the level moving)
 * quickens the ripples and brightens the foam, so a rise or a fall is seen; `uTremble` sends rings out from the island
 * (the knights' fists on their shields). (Fill rate: the basin covers most of a phone's screen; two noises and a sine
 * per pixel, no texture.)
 */
const waterMat = (): THREE.ShaderMaterial =>
  new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    uniforms: {
      time: { value: 0 },
      uLevel: { value: new THREE.Vector4(-9, -9, -9, -9) },
      uTremble: { value: 0 },
      uTrembleT: { value: 0 },
      uStir: { value: 0 },
      uCalm: { value: 0 },
      // (Raw values: the shader's output is taken to sRGB, so these are much darker than they look as hex.)
      sky: { value: new THREE.Color('#2c4c78') },
      shallow: { value: new THREE.Color('#123050') },
      deep: { value: new THREE.Color('#050c18') },
    },
    vertexShader: /* glsl */ `
      attribute float aQ;
      uniform vec4 uLevel;
      varying vec3 vW;
      varying float vLv;
      void main() {
        float lv = aQ < 0.5 ? uLevel.x : aQ < 1.5 ? uLevel.y : aQ < 2.5 ? uLevel.z : uLevel.w;
        vec4 w = modelMatrix * vec4(position.x, lv, position.z, 1.0);
        vW = w.xyz;
        vLv = lv;
        gl_Position = projectionMatrix * viewMatrix * w;
      }`,
    fragmentShader: /* glsl */ `
      uniform float time;
      uniform float uTremble;
      uniform float uTrembleT;
      uniform float uStir;
      uniform float uCalm;
      uniform vec3 sky;
      uniform vec3 shallow;
      uniform vec3 deep;
      varying vec3 vW;
      varying float vLv;
      ${GLSL_N}
      ${GLSL_CONE}
      void main() {
        float r = length(vW.xz);
        // (The floor under it: the cone, then the bank's rise to the rim, so a brim over five marks has its foam too.)
        float bed = r < ${(BR - BANK).toFixed(4)} ? svCone(r) : svCone(r) * (1.0 - smoothstep(${(BR - BANK).toFixed(4)}, ${BR.toFixed(4)}, r));
        float depth = vLv - bed;
        if (depth < -0.01 || vLv < -8.0) discard;
        vec3 v = normalize(cameraPosition - vW);
        float fr = pow(1.0 - clamp(v.y, 0.0, 1.0), 2.0);
        float n = svF(vW.xz * 1.3 + vec2(time * 0.05, -time * 0.03));
        // The body: clearer over the shallows, dark where deep; the cold of the vault in it toward the eye.
        vec3 c = mix(shallow, deep, smoothstep(0.1, 1.3, depth));
        c = mix(c, sky, 0.12 + fr * 0.4 + (n - 0.5) * 0.2);
        // The moon down the well: a pale disc on the water round the island, broken by the ripples.
        float shaft = 1.0 - smoothstep(1.2, 3.8, r);
        c += vec3(0.12, 0.16, 0.26) * shaft * (0.35 + 0.5 * smoothstep(0.45, 0.8, n));
        // Slow cold ripples out from the island, a glint riding them.
        // (Fine and sparse: thick bright bands read as whitecaps, a milky sheet from a scene's low camera. In a scene,
        // uCalm, the water is still: the ripples slower and fainter.)
        float sp = (0.9 + uStir * 2.4) * (1.0 - 0.5 * uCalm);
        float band = sin(r * 7.5 - time * sp + n * 4.0);
        float hl = smoothstep(0.93, 0.995, band) * smoothstep(0.45, 0.75, n);
        float gl = svN(vW.xz * 5.0 + vec2(time * 0.6, -time * 0.45) * (1.0 - 0.6 * uCalm));
        hl += smoothstep(0.88, 0.97, gl * n * 1.7) * 0.6;
        c += vec3(0.16, 0.22, 0.34) * hl * (0.32 + 0.5 * uStir) * (1.0 - 0.55 * uCalm) * (1.0 - 0.6 * fr);
        // The tremble: rings running out from the island.
        float tr = uTremble * sin(r * 11.0 - uTrembleT * 9.0) * (1.0 - smoothstep(0.0, uTrembleT * 2.6 + 1.0, abs(r - uTrembleT * 2.2 - 2.0)));
        c += vec3(0.4, 0.46, 0.62) * max(0.0, tr) * 0.5;
        // The island drowned: its curb a pale ring under the water, its top paler (shallow over it).
        float isle = vLv - ${ISLE_Y.toFixed(4)};
        if (isle > 0.0) {
          float over = 1.0 - smoothstep(${(IR - 0.2).toFixed(4)}, ${(IR + 0.05).toFixed(4)}, r);
          c = mix(c, vec3(0.1, 0.16, 0.25), over * (1.0 - smoothstep(0.0, 1.4, isle)) * 0.7);
          float curb = 1.0 - smoothstep(0.04, 0.2, abs(r - ${(IR - 0.02).toFixed(4)}));
          c += vec3(0.22, 0.3, 0.44) * curb * (0.55 + 0.25 * sin(time * 1.3 + n * 6.0)) * (1.0 - smoothstep(0.4, 1.6, isle));
        }
        // Foam where it meets the stone: a bright cold line, brighter while the level moves.
        float shore = 1.0 - smoothstep(0.0, 0.1, depth);
        float lap = 0.75 + 0.25 * sin(time * 2.1 + r * 9.0 + n * 4.0);
        c = mix(c, vec3(0.46, 0.56, 0.72), shore * lap * (0.75 + 0.25 * uStir));
        float a = mix(0.6, 0.92, smoothstep(0.0, 0.45, depth));
        gl_FragColor = vec4(c, max(a, shore * 0.85));
        #include <colorspace_fragment>
      }`,
  });

/** A ribbon's flowing water (the channels while a gate runs): `aSlot` the gate, `uFlow` how much. */
const flowMat = (): THREE.ShaderMaterial =>
  new THREE.ShaderMaterial({
    transparent: true,
    side: THREE.DoubleSide,
    depthWrite: false,
    polygonOffset: true,
    polygonOffsetFactor: -3,
    polygonOffsetUnits: -3,
    uniforms: { time: { value: 0 }, uFlow: { value: new THREE.Vector4() } },
    vertexShader: /* glsl */ `
      attribute float aSlot;
      uniform vec4 uFlow;
      varying vec2 vUv;
      varying float vF;
      void main() {
        vUv = uv;
        vF = aSlot < 0.5 ? uFlow.x : aSlot < 1.5 ? uFlow.y : aSlot < 2.5 ? uFlow.z : uFlow.w;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }`,
    fragmentShader: /* glsl */ `
      uniform float time;
      varying vec2 vUv;
      varying float vF;
      ${GLSL_N}
      void main() {
        if (vF < 0.01) discard;
        float run = svN(vec2(vUv.x * 4.0, vUv.y * 3.0 - time * 3.4)) * 0.6 + svN(vec2(vUv.x * 9.0, vUv.y * 7.0 - time * 5.2)) * 0.4;
        vec3 c = mix(vec3(0.06, 0.09, 0.13), vec3(0.4, 0.48, 0.62), 0.3 + 0.5 * run);
        c += vec3(0.75, 0.8, 0.92) * smoothstep(0.66, 0.95, run) * 0.5;
        float across = 1.0 - abs(vUv.x - 0.5) * 2.0;
        gl_FragColor = vec4(c, smoothstep(0.0, 0.3, across) * 0.9 * vF);
        #include <colorspace_fragment>
      }`,
  });

/** The salt runnels: the groove dark, white with salt (and a little lit, so it reads at night) when salted; the curbs plain. */
const runnelMat = (salt: THREE.IUniform<number[]>, u: StoneU): THREE.MeshStandardMaterial => {
  // (Its ribbons wind either way: drawn from both sides.)
  const m = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.8, metalness: 0.02, side: THREE.DoubleSide });
  m.onBeforeCompile = (shader) => {
    Object.assign(shader.uniforms, u);
    shader.uniforms.uSalt = salt;
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nattribute float aRun;\nuniform float uSalt[8];\nvarying float vSalt;\nvarying vec3 vSvW;')
      .replace('#include <begin_vertex>', '#include <begin_vertex>\nvSalt = aRun > -0.5 ? uSalt[int(aRun + 0.5)] : 0.0;\nvSvW = (modelMatrix * vec4(transformed, 1.0)).xyz;');
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', `#include <common>\nuniform float uTime;\nvarying float vSalt;\nvarying vec3 vSvW;\n${GLSL_N}`)
      .replace(
        '#include <color_fragment>',
        `#include <color_fragment>
float svSparkle = 0.0;
if (vSalt > 0.01) {
  float g = svH(floor(vSvW.xz * 40.0));
  vec3 salt = vec3(0.92, 0.94, 0.96) * (0.85 + 0.15 * g);
  diffuseColor.rgb = mix(diffuseColor.rgb, salt, vSalt);
  svSparkle = step(0.96, g) * (0.5 + 0.5 * sin(uTime * 5.0 + g * 40.0)) * vSalt;
}`,
      )
      .replace('#include <emissivemap_fragment>', '#include <emissivemap_fragment>\ntotalEmissiveRadiance += vec3(0.32, 0.35, 0.4) * vSalt + vec3(0.8) * svSparkle;');
  };
  m.customProgramCacheKey = () => 'somnhold-sev-runnel';
  return m;
};

/** La Que Huyó's line on the floor: cold white, dashes running toward Marit, brighter as the run comes. */
const lineMat = (thick: boolean): THREE.ShaderMaterial =>
  new THREE.ShaderMaterial({
    transparent: true,
    side: THREE.DoubleSide,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    uniforms: { time: { value: 0 }, show: { value: 0 }, urge: { value: 0 } },
    vertexShader: /* glsl */ `
      varying vec2 vUv;
      void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`,
    fragmentShader: /* glsl */ `
      uniform float time;
      uniform float show;
      uniform float urge;
      varying vec2 vUv;
      void main() {
        float across = 1.0 - abs(vUv.x - 0.5) * 2.0;
        float core = smoothstep(0.35, 0.9, across);
        float edge = smoothstep(0.0, 0.5, across);
        float dash = 0.55 + 0.45 * smoothstep(0.3, 0.7, fract(vUv.y * ${thick ? '1.4' : '2.2'} - time * (1.4 + urge * 2.5)));
        float a = (edge * 0.6 + core * 1.2) * dash * show * (0.8 + 0.4 * urge);
        vec3 c = mix(vec3(0.62, 0.74, 1.0), vec3(1.0), core);
        gl_FragColor = vec4(c * a, a);
        #include <colorspace_fragment>
      }`,
  });

/** The moon's shaft down the old well onto the island: a soft cone of cold light, its haze drifting. */
const shaftMat = (): THREE.ShaderMaterial =>
  new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    side: THREE.DoubleSide,
    blending: THREE.AdditiveBlending,
    uniforms: { time: { value: 0 }, strength: { value: 1 } },
    vertexShader: /* glsl */ `
      varying vec2 vUv;
      varying vec3 vW;
      void main() { vUv = uv; vec4 w = modelMatrix * vec4(position, 1.0); vW = w.xyz; gl_Position = projectionMatrix * viewMatrix * w; }`,
    fragmentShader: /* glsl */ `
      uniform float time;
      uniform float strength;
      varying vec2 vUv;
      varying vec3 vW;
      ${GLSL_N}
      void main() {
        float y = vUv.y;
        float body = smoothstep(0.0, 0.2, y) * (1.0 - smoothstep(0.3, 1.0, y));
        float a = (0.05 + 0.04 * svN(vec2(vUv.x * 7.0 + time * 0.1, y * 3.0))) * body * strength;
        gl_FragColor = vec4(vec3(0.72, 0.8, 1.0) * a, a);
        #include <colorspace_fragment>
      }`,
  });

/** Decals on the floor and the water: a print, a ripple ring, a soft disc. */
const D_PRINT = 0;
const D_RIPPLE = 1;
const D_SOFT = 2;
const decalMat = (): THREE.ShaderMaterial =>
  new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    polygonOffset: true,
    polygonOffsetFactor: -6,
    polygonOffsetUnits: -6,
    uniforms: { time: { value: 0 } },
    vertexShader: /* glsl */ `
      attribute float aKind;
      attribute float aAlpha;
      varying float vKind;
      varying float vAlpha;
      varying vec2 vUv;
      varying vec3 vC;
      void main() {
        vKind = aKind; vAlpha = aAlpha; vUv = uv; vC = instanceColor;
        gl_Position = projectionMatrix * viewMatrix * modelMatrix * instanceMatrix * vec4(position, 1.0);
      }`,
    fragmentShader: /* glsl */ `
      varying float vKind;
      varying float vAlpha;
      varying vec2 vUv;
      varying vec3 vC;
      void main() {
        vec2 p = vUv - 0.5;
        float r = length(p) * 2.0;
        float a = 0.0;
        if (vKind < 0.5) {
          vec2 f = p * vec2(3.4, 1.7);
          float sole = 1.0 - smoothstep(0.33, 0.43, length(f - vec2(0.0, 0.08)));
          float heel = 1.0 - smoothstep(0.24, 0.32, length((p - vec2(0.0, -0.22)) * vec2(3.0, 2.6)));
          a = max(sole, heel);
        } else if (vKind < 1.5) {
          a = (1.0 - smoothstep(0.0, 0.09, abs(r - 0.82))) + (1.0 - smoothstep(0.0, 0.12, abs(r - 0.5))) * 0.4;
        } else {
          a = (1.0 - smoothstep(0.0, 1.0, r)) * 0.8;
        }
        gl_FragColor = vec4(vC, clamp(a, 0.0, 1.0) * vAlpha);
        #include <colorspace_fragment>
      }`,
  });

/* ---------- helpers ---------- */

/** A small seeded random (the dressing is the same every time). */
const rand = (seed: number): (() => number) => {
  let s = seed >>> 0 || 1;
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 4294967296;
  };
};

/** Colours of the stone and its kin. */
const DARK = ['#4c5058', '#555a62', '#474b53', '#5c6068'];
const PALE = ['#9a9fa6', '#a4a9af', '#9095a0', '#adb1b6'];
const SLATE = '#2c3038';
const IRON = '#262a31';
const WOOD = '#3e342e';
const M_PLAIN = { aMason: 0 };
const M_ASHLAR = { aMason: 1 };
const M_OLD = { aMason: 2 };

/**
 * The Way's banner: white cloth on a crossbar, the open eye in dragon red (pieces into `kit` at `m`; the banner hangs
 * from y 0.62 to -0.75). With `cloth`, the white goes there instead (the bastion's trophy: its own cold-lit material,
 * so the keep's warm fire never turns it pink or beige).
 */
export const wayBanner = (kit: Kit, m: THREE.Matrix4, vals: Record<string, number> = M_PLAIN, cloth?: Kit): void => {
  const g = new THREE.BoxGeometry(1, 1, 1);
  const ring = new THREE.TorusGeometry(0.2, 0.035, 4, 18);
  const disc = new THREE.CircleGeometry(0.08, 12);
  const dot = new THREE.CircleGeometry(0.03, 8);
  const l = (x: number, y: number, z: number, sx: number, sy: number, sz: number, rz = 0): THREE.Matrix4 => place(x, y, z, 0, new THREE.Vector3(sx, sy, sz), 0, rz);
  const w = cloth ?? kit;
  kit.add(g, '#2a2e35', m, l(0, 0.62, 0, 1.0, 0.05, 0.05), vals);
  w.add(g, '#e9edf2', m, l(0, 0, 0, 0.86, 1.2, 0.025), vals);
  w.add(g, '#e9edf2', m, l(-0.22, -0.66, 0, 0.43, 0.18, 0.025, 0.35), vals);
  w.add(g, '#e9edf2', m, l(0.22, -0.66, 0, 0.43, 0.18, 0.025, -0.35), vals);
  kit.add(ring, '#d23c2c', m, l(0, 0.05, 0.02, 1, 0.55, 1), vals);
  kit.add(disc, '#ff4f3a', m, l(0, 0.05, 0.022, 1, 1, 1), vals);
  kit.add(dot, '#1a1c20', m, l(0, 0.05, 0.024, 1, 1, 1), vals);
  // The salt's white border and the Way's seven eyes in a row under it, small.
  w.add(g, '#c9d2dc', m, l(0, -0.5, 0.016, 0.8, 0.03, 0.01), vals);
  for (let i = 0; i < 7; i++) kit.add(dot, '#ff4f3a', m, l(-0.3 + i * 0.1, -0.42, 0.022, 0.9, 0.55, 1), vals);
  g.dispose();
  ring.dispose();
  disc.dispose();
  dot.dispose();
};

/**
 * Marit's old shield (the paladin's chapter 5 trophy, «Juro yo»): a heater of pale worn steel, its rim dented, the first
 * Vigil's eye on it in red, the strap hanging; pieces into `kit` at `m` (its face toward +z, about 1.2 tall).
 */
export const maritShield = (kit: Kit, m: THREE.Matrix4, vals: Record<string, number> = M_PLAIN): void => {
  const g = new THREE.BoxGeometry(1, 1, 1);
  const ring = new THREE.TorusGeometry(0.2, 0.04, 4, 18);
  const disc = new THREE.CircleGeometry(0.085, 12);
  const l = (x: number, y: number, z: number, sx: number, sy: number, sz: number, rz = 0): THREE.Matrix4 => place(x, y, z, 0, new THREE.Vector3(sx, sy, sz), 0, rz);
  kit.add(g, '#5a5f68', m, l(0, 0.16, -0.03, 0.9, 0.62, 0.05), vals);
  kit.add(g, '#5a5f68', m, l(0, -0.26, -0.03, 0.64, 0.64, 0.05, Math.PI / 4), vals);
  kit.add(g, '#c3c8cf', m, l(0, 0.16, 0, 0.82, 0.55, 0.06), vals);
  kit.add(g, '#c3c8cf', m, l(0, -0.26, 0, 0.57, 0.57, 0.06, Math.PI / 4), vals);
  // Scars across it.
  kit.add(g, '#7e848c', m, l(-0.12, 0.05, 0.032, 0.36, 0.022, 0.01, 0.5), vals);
  kit.add(g, '#7e848c', m, l(0.16, -0.2, 0.032, 0.26, 0.02, 0.01, -0.7), vals);
  kit.add(ring, '#c23224', m, l(0, 0.08, 0.035, 1, 0.55, 1), vals);
  kit.add(disc, '#ff4f3a', m, l(0, 0.08, 0.038, 1, 1, 1), vals);
  kit.add(g, '#2e2620', m, l(0.34, -0.5, -0.06, 0.05, 0.4, 0.02, 0.2), vals);
  g.dispose();
  ring.dispose();
  disc.dispose();
};

/** A model of the path's people merged into a kit at `m` (its colours from its materials): figures that only stand. */
export const mergeFigure = (kit: Kit, id: string, m: THREE.Matrix4): void => {
  const root = sendaModel(id).root;
  root.updateMatrixWorld(true);
  const tmp = new THREE.Color();
  const mm = new THREE.Matrix4();
  root.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (!mesh.isMesh || !mesh.geometry) return;
    const mat = (Array.isArray(mesh.material) ? mesh.material[0] : mesh.material) as THREE.MeshStandardMaterial | undefined;
    if (!mat || mat.transparent) return;
    tmp.set(mat.color ?? '#cccccc');
    mm.multiplyMatrices(m, mesh.matrixWorld);
    kit.add(mesh.geometry, tmp, mm);
  });
  root.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (mesh.isMesh) mesh.geometry?.dispose();
  });
};

/** The monastery dam's great wheel (wood and iron), upright, its axle along local x: the pieces into `kit` at `m`. */
export const damWheel = (kit: Kit, m: THREE.Matrix4, vals?: Record<string, number>): void => {
  const box = new THREE.BoxGeometry(1, 1, 1);
  const ring = new THREE.TorusGeometry(1, 0.08, 5, 22);
  const l = (x: number, y: number, z: number, sx: number, sy: number, sz: number, rx = 0): THREE.Matrix4 =>
    new THREE.Matrix4().compose(new THREE.Vector3(x, y, z), new THREE.Quaternion().setFromEuler(new THREE.Euler(rx, 0, 0)), new THREE.Vector3(sx, sy, sz));
  const ry = new THREE.Matrix4().makeRotationY(Math.PI / 2);
  for (const x of [-0.12, 0.12]) kit.add(ring, '#4a3c33', m, new THREE.Matrix4().multiplyMatrices(new THREE.Matrix4().makeTranslation(x, 0, 0), ry), vals);
  for (let i = 0; i < 8; i++) {
    const a = (i / 8) * Math.PI;
    kit.add(box, '#3e322b', m, l(0, 0, 0, 0.06, 1.95, 0.07, a), vals);
  }
  // Paddles round the rim, the iron hub.
  for (let i = 0; i < 16; i++) {
    const a = (i / 16) * Math.PI * 2;
    kit.add(box, '#52443a', m, l(0, Math.cos(a) * 1.02, Math.sin(a) * 1.02, 0.34, 0.05, 0.22, a), vals);
  }
  kit.add(box, IRON, m, l(0, 0, 0, 0.5, 0.18, 0.18), vals);
  box.dispose();
  ring.dispose();
};

/** A polar disc of the ground (rings at the given radii, `segs` round), its heights from `y`: merged into one geometry. */
const polarDisc = (radii: number[], segs: number, y: (x: number, z: number) => number): THREE.BufferGeometry => {
  const pos: number[] = [];
  const idx: number[] = [];
  for (let i = 0; i < radii.length; i++) {
    const r = radii[i] as number;
    for (let j = 0; j <= segs; j++) {
      const a = (j / segs) * Math.PI * 2;
      const x = Math.cos(a) * r;
      const z = Math.sin(a) * r;
      pos.push(x, y(x, z), z);
    }
  }
  const W = segs + 1;
  for (let i = 0; i < radii.length - 1; i++)
    for (let j = 0; j < segs; j++) {
      const a = i * W + j;
      const b = a + W;
      idx.push(a, a + 1, b, a + 1, b + 1, b);
    }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setIndex(idx);
  g.computeVertexNormals();
  return g;
};

/** Ribbons along polylines (each point's height given), merged: uv across (x) and along (y, world units); a float per ribbon. */
const ribbons = (parts: { pts: THREE.Vector3[]; w: number; v: number }[], attr: string): THREE.BufferGeometry => {
  const pos: number[] = [];
  const uv: number[] = [];
  const val: number[] = [];
  const idx: number[] = [];
  for (const part of parts) {
    const base = pos.length / 3;
    let len = 0;
    part.pts.forEach((p, i) => {
      const a = part.pts[Math.max(0, i - 1)] as THREE.Vector3;
      const b = part.pts[Math.min(part.pts.length - 1, i + 1)] as THREE.Vector3;
      const dx = b.x - a.x;
      const dz = b.z - a.z;
      const l = Math.hypot(dx, dz) || 1;
      const nx = -dz / l;
      const nz = dx / l;
      if (i > 0) len += p.distanceTo(part.pts[i - 1] as THREE.Vector3);
      pos.push(p.x + (nx * part.w) / 2, p.y, p.z + (nz * part.w) / 2, p.x - (nx * part.w) / 2, p.y, p.z - (nz * part.w) / 2);
      uv.push(0, len, 1, len);
      val.push(part.v, part.v);
      if (i > 0) {
        const k = base + i * 2;
        idx.push(k - 2, k - 1, k, k - 1, k + 1, k);
      }
    });
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
  g.setAttribute(attr, new THREE.Float32BufferAttribute(val, 1));
  g.setIndex(idx);
  g.computeVertexNormals();
  return g;
};

/** The gates' directions from the middle (sim order: the yard SW, the barracks NW, the forge NE, the ravine SE), world x, z. */
const GATE_DIR: [number, number][] = [
  [-Math.SQRT1_2, Math.SQRT1_2],
  [-Math.SQRT1_2, -Math.SQRT1_2],
  [Math.SQRT1_2, -Math.SQRT1_2],
  [Math.SQRT1_2, Math.SQRT1_2],
];

/** The yard's torches (world x, z; on the keep's face at y 2.2 when `wall`). */
const YARD_TORCHES: { x: number; z: number; wall?: boolean }[] = [
  { x: -1.8, z: KS + 0.12, wall: true },
  { x: 1.8, z: KS + 0.12, wall: true },
  { x: -5.6, z: KS + 0.2, wall: true },
  { x: 5.6, z: KS + 0.2, wall: true },
  { x: -7.4, z: -1.2 },
  { x: 7.4, z: -1.2 },
  { x: -8.6, z: 4.6 },
  { x: 8.6, z: 4.6 },
  { x: -4.6, z: 8.8 },
  { x: 4.6, z: 8.8 },
  { x: -2.4, z: WALL_R - 1.2 },
  { x: 2.4, z: WALL_R - 1.2 },
];

type LineRib = { mesh: THREE.Mesh; mat: THREE.ShaderMaterial; key: string; show: number };

export class SeventhLayer {
  readonly group = new THREE.Group();
  private mats: THREE.Material[] = [];
  private geos: THREE.BufferGeometry[] = [];
  private glow = new GlowLayer(220);
  private dome: SkyDome | null = null;
  private su: StoneU = { uTime: { value: 0 } };
  private fu: FloorU = { uTime: { value: 0 }, uStair: { value: 0 } };
  private now = 0;
  private lastTick = -1;
  private low: boolean;
  private kind: SeventhRules['kind'] | null;
  private scene = false;
  // The yard.
  private prog: THREE.IUniform<number[]> = { value: [0, 0, 0, 0, 0, 0] };
  private stair: THREE.Group | null = null;
  private stairShown = 0;
  private torchAt: THREE.Vector3[] = [];
  private windows: THREE.Vector3[] = [];
  private carried: { x: number; y: number; z: number }[] = [];
  // The cistern.
  private water: { mesh: THREE.Mesh; mat: THREE.ShaderMaterial; shown: number[]; want: number[] } | null = null;
  private flow: { mat: THREE.ShaderMaterial; f: number[] } | null = null;
  private wheels: THREE.InstancedMesh | null = null;
  private leaves: THREE.InstancedMesh | null = null;
  private gateUp = [0, 0, 0, 0];
  private gateSpin = [0, 0, 0, 0];
  private gateRun = [0, 0, 0, 0];
  private gateAng = [0, 0, 0, 0];
  private shakeUntil = [0, 0, 0, 0];
  private salt: THREE.IUniform<number[]> = { value: [0, 0, 0, 0, 0, 0, 0, 0] };
  private saltWant = [0, 0, 0, 0, 0, 0, 0, 0];
  private lines: LineRib[] = [];
  private decals: THREE.InstancedMesh | null = null;
  private eye: { lid: THREE.Mesh; white: THREE.Mesh; iris: THREE.Mesh; at: THREE.Vector3; open: number; want: number } | null = null;
  private eyesSeven: THREE.Vector3[] = [];
  private oath = 0;
  private oathShown = 0;
  private shaft: THREE.ShaderMaterial | null = null;
  private shaftK = 0.3;
  private braziers: THREE.Vector3[] = [];
  private tremble = -1;
  private flashUntil = 0;
  readonly anchors: {
    piles: THREE.Vector3[];
    gates: THREE.Vector3[];
    runnels: THREE.Vector3[];
    holes: THREE.Vector3[];
    altar: THREE.Vector3 | null;
    island: THREE.Vector3 | null;
    stair: THREE.Vector3 | null;
  } = { piles: [], gates: [], runnels: [], holes: [], altar: null, island: null, stair: null };

  constructor(
    private ctx: OmenCtx,
    private map: MapDef,
    readonly place: string,
    s: BattleState,
  ) {
    this.group.name = `seventh-${place}`;
    this.low = ctx.low;
    const r = s.rules?.senda?.seventh;
    this.kind = r?.kind ?? null;
    if (place === 'cistern7') this.buildCistern(r);
    else if (place === 'keepTop') this.buildTop();
    else this.buildYard(r);
    this.buildDecals();
    this.glow.mesh.frustumCulled = false;
    this.group.add(this.glow.mesh);
  }

  private get look(): Place {
    return (SEVENTH_PLACES.has(this.place) ? this.place : 'keepNight') as Place;
  }

  /** The ground where the pieces stand. */
  private ground(x: number, z: number): number {
    if (this.place === 'cistern7') return cistGround(x, z);
    return 0;
  }

  /* ---------- the board, repainted ---------- */

  apply(terrain: TerrainBuild): void {
    terrain.mesh.visible = false;
    terrain.decor.visible = false;
    if (terrain.fog) terrain.fog.group.visible = false;
    terrain.ground = (x, z) => this.ground(x, z);
    const R = this.map.radius;
    const W = 2 * R + 1;
    for (let r = -R; r <= R; r++)
      for (let q = -R; q <= R; q++) {
        const id = (r + R) * W + (q + R);
        if (id < 0 || id >= terrain.heights.length) continue;
        terrain.heights[id] = this.ground((q * 1000 + r * 500) * K, r * 866 * K);
        terrain.terrain[id] = T_PATH;
      }
    if (HOURS[this.look].sky) {
      this.dome = new SkyDome();
      this.group.add(this.dome.mesh);
    }
    this.paint();
  }

  /** The place's own light (each place has one hour; a scene's sky agrees with it). */
  paint(): void {
    const look = HOURS[this.look];
    const scene = this.ctx.scene;
    scene.background = new THREE.Color(look.bg);
    if (scene.fog instanceof THREE.Fog) {
      scene.fog.color.set(look.fog);
      scene.fog.near = look.near;
      scene.fog.far = look.far;
    }
    const { hemi, key, rim } = this.ctx;
    if (hemi) {
      hemi.color.set(look.hemi[0]);
      hemi.groundColor.set(look.hemi[1]);
      hemi.intensity = look.hemi[2];
    }
    if (key) {
      key.color.set(look.key[0]);
      key.intensity = look.key[1];
    }
    if (rim) {
      rim.color.set(look.rim[0]);
      rim.intensity = look.rim[1];
    }
    if (this.dome && look.sky) {
      this.dome.set(look.sky[0], look.sky[1], look.sky[2]);
      this.dome.u.uSunDir.value.set(look.sun[0], look.sun[1], look.sun[2]).normalize();
    }
  }

  /** A scene's moment: the knights kneel (the water trembles), the altar's eye opens; `scene` marks a cinematic. */
  cue(c: 'kneel' | 'eye' | 'scene'): void {
    if (c === 'scene') {
      this.scene = true;
      // (A scene's camera looks along the shaft, low; a fight's looks down through it: there it is only a breath.)
      this.shaftK = 1;
      // (The edge's «still water»: in a scene it barely ripples.)
      if (this.water) (this.water.mat.uniforms.uCalm as THREE.IUniform<number>).value = 1;
    }
    else if (c === 'kneel') {
      this.tremble = this.now;
      this.ctx.shake(0.12);
      if (this.anchors.island) this.ctx.vfx.ring(this.anchors.island.clone().setY(markY(3) + 0.03), 0.4, 3.2, '#dfe8ff', 10, 0.6);
    } else if (c === 'eye' && this.eye) {
      this.eye.want = 1;
      this.oath = 1;
    }
  }

  /* ---------- the yard ---------- */

  private box(kit: Kit, x0: number, x1: number, z0: number, z1: number, y0: number, y1: number, color: string, m: Record<string, number> = M_ASHLAR, yaw = 0): void {
    const g = new THREE.BoxGeometry(1, 1, 1);
    kit.add(g, color, place((x0 + x1) / 2, (y0 + y1) / 2, (z0 + z1) / 2, yaw, new THREE.Vector3(Math.abs(x1 - x0), Math.abs(y1 - y0), Math.abs(z1 - z0))), undefined, m);
    g.dispose();
  }

  /** Merlons along a run from (x0, z0) to (x1, z1) at height y. */
  private crenels(kit: Kit, x0: number, z0: number, x1: number, z1: number, y: number, color: string, size = 0.45): void {
    const len = Math.hypot(x1 - x0, z1 - z0);
    const n = Math.max(1, Math.floor(len / (size * 2)));
    const yaw = Math.atan2(x1 - x0, z1 - z0);
    const g = new THREE.BoxGeometry(1, 1, 1);
    for (let i = 0; i < n; i++) {
      const t = (i + 0.5) / n;
      kit.add(g, color, place(x0 + (x1 - x0) * t, y + size * 0.6, z0 + (z1 - z0) * t, yaw, new THREE.Vector3(size * 0.8, size * 1.2, size)), undefined, M_ASHLAR);
    }
    g.dispose();
  }

  /**
   * The bastion round a keep: the curtain wall in a ring (towers, the gate to the south), the barracks and the stores
   * along it. `y0` is the yard's height, (cx, cz) its middle.
   */
  private buildBastion(kit: Kit, lit: Kit, y0: number, cx: number, cz: number, gate = Math.PI / 2): void {
    const g = new THREE.BoxGeometry(1, 1, 1);
    const cyl = new THREE.CylinderGeometry(1, 1.06, 1, 12);
    const cone = new THREE.ConeGeometry(1, 1, 12);
    const N = 40;
    for (let i = 0; i < N; i++) {
      const a0 = (i / N) * Math.PI * 2;
      const a1 = ((i + 1) / N) * Math.PI * 2;
      const am = (a0 + a1) / 2;
      // (The gate to the south: open between its towers.)
      if (Math.abs(Math.atan2(Math.sin(am - gate), Math.cos(am - gate))) < 0.1) continue;
      const x = cx + Math.cos(am) * WALL_R;
      const z = cz + Math.sin(am) * WALL_R;
      const len = WALL_R * (a1 - a0) + 0.05;
      const yaw = -am + Math.PI / 2;
      kit.add(g, DARK[i % 4] as string, place(x, y0 + 1.7, z, yaw + Math.PI / 2, new THREE.Vector3(1.2, 3.4, len)), undefined, M_ASHLAR);
      const ox = Math.cos(a0) * WALL_R + cx;
      const oz = Math.sin(a0) * WALL_R + cz;
      const ex = Math.cos(a1) * WALL_R + cx;
      const ez = Math.sin(a1) * WALL_R + cz;
      this.crenels(kit, ox, oz, ex, ez, y0 + 3.4, DARK[(i + 1) % 4] as string, 0.42);
    }
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2 + gate;
      for (const off of i === 0 ? [-0.12, 0.12] : [0]) {
        const x = cx + Math.cos(a + off) * WALL_R;
        const z = cz + Math.sin(a + off) * WALL_R;
        kit.add(cyl, DARK[i % 4] as string, place(x, y0 + 2.4, z, 0, new THREE.Vector3(1.5, 4.8, 1.5)), undefined, M_ASHLAR);
        kit.add(cone, SLATE, place(x, y0 + 5.4, z, 0, new THREE.Vector3(1.75, 1.3, 1.75)), undefined, M_PLAIN);
        lit.add(g, '#ff9a5a', place(x - Math.cos(a) * 1.45, y0 + 3.3, z - Math.sin(a) * 1.45, -a + Math.PI / 2, new THREE.Vector3(0.12, 0.34, 0.06)));
        this.windows.push(new THREE.Vector3(x - Math.cos(a) * 1.5, y0 + 3.3, z - Math.sin(a) * 1.5));
      }
    }
    // The barracks along the west wall and the forge's block on the east: long, low, slate roofs, a lit window or two.
    for (const side of [-1, 1]) {
      const x = cx + side * 15.2;
      const z = cz + 1.5;
      this.box(kit, x - 1.8, x + 1.8, z - 6, z + 6, y0, y0 + 2.4, DARK[side > 0 ? 1 : 2] as string);
      for (const k of [-1, 1]) {
        const roof = new THREE.BoxGeometry(1, 1, 1);
        kit.add(roof, SLATE, place(x + k * 1.0, y0 + 2.95, z, 0, new THREE.Vector3(2.3, 0.12, 12.4), 0, k * 0.55), undefined, M_PLAIN);
        roof.dispose();
      }
      for (let j = 0; j < 4; j++) {
        if ((j + (side > 0 ? 1 : 0)) % 2) continue;
        const wz = z - 4.5 + j * 3;
        lit.add(g, '#ff8a52', place(x - side * 1.82, y0 + 1.3, wz, Math.PI / 2, new THREE.Vector3(0.36, 0.5, 0.04)));
        this.windows.push(new THREE.Vector3(x - side * 1.9, y0 + 1.3, wz));
      }
    }
    g.dispose();
    cyl.dispose();
    cone.dispose();
  }

  /** The keep: a tall tower of dark stone over the yard, its block where the sim's is, crenels, corner turrets, lit slits. */
  private buildKeep(kit: Kit, lit: Kit, y0: number, x0: number, x1: number, z0: number, z1: number, top: number, crown = true): void {
    const cyl = new THREE.CylinderGeometry(1, 1.04, 1, 12);
    const cone = new THREE.ConeGeometry(1, 1, 12);
    const g = new THREE.BoxGeometry(1, 1, 1);
    // A battered plinth, the body, a string course every few metres.
    this.box(kit, x0 - 0.25, x1 + 0.25, z0 - 0.25, z1 + 0.25, y0, y0 + 1.3, DARK[3] as string);
    this.box(kit, x0, x1, z0, z1, y0 + 1.3, y0 + top, DARK[1] as string);
    for (const h of [4.2, 8.4]) this.box(kit, x0 - 0.1, x1 + 0.1, z0 - 0.1, z1 + 0.1, y0 + h, y0 + h + 0.18, DARK[3] as string);
    // Machicolations under the parapet, the parapet and its merlons (the keep's top, when it is not the scene's own).
    if (crown) this.box(kit, x0 - 0.35, x1 + 0.35, z0 - 0.35, z1 + 0.35, y0 + top - 0.05, y0 + top + 0.55, DARK[2] as string);
    if (crown)
      for (const [ax, az, bx, bz] of [
      [x0 - 0.2, z1 + 0.2, x1 + 0.2, z1 + 0.2],
      [x0 - 0.2, z0 - 0.2, x1 + 0.2, z0 - 0.2],
      [x0 - 0.2, z0 - 0.2, x0 - 0.2, z1 + 0.2],
      [x1 + 0.2, z0 - 0.2, x1 + 0.2, z1 + 0.2],
    ] as const)
      this.crenels(kit, ax, az, bx, bz, y0 + top + 0.55, DARK[0] as string, 0.5);
    for (const [cx, cz] of [
      [x0, z0],
      [x1, z0],
      [x0, z1],
      [x1, z1],
    ] as const) {
      kit.add(cyl, DARK[0] as string, place(cx, y0 + (top + 2.2) / 2, cz, 0, new THREE.Vector3(1.05, top + 2.2, 1.05)), undefined, M_ASHLAR);
      kit.add(cone, SLATE, place(cx, y0 + top + 3.0, cz, 0, new THREE.Vector3(1.3, 1.7, 1.3)), undefined, M_PLAIN);
    }
    // Slits on the south face (a few lit), the great door up its stair on the west.
    for (let row = 0; row < 3; row++)
      for (let i = 0; i < 4; i++) {
        const x = x0 + ((i + 0.5) / 4) * (x1 - x0);
        const y = y0 + 3 + row * 3.6;
        const on = (row * 4 + i) % 3 === 1;
        if (on) {
          lit.add(g, '#ff9050', place(x, y, z1 + 0.02, 0, new THREE.Vector3(0.18, 0.7, 0.05)));
          this.windows.push(new THREE.Vector3(x, y, z1 + 0.1));
        } else this.box(kit, x - 0.09, x + 0.09, z1 - 0.02, z1 + 0.03, y - 0.35, y + 0.35, '#0c0e12', M_PLAIN);
      }
    g.dispose();
    cyl.dispose();
    cone.dispose();
  }

  /** The torches: an iron post and basket (or a wall bracket), their fire a glow. */
  private buildTorch(kit: Kit, lit: Kit, x: number, z: number, y0: number, wall: boolean): void {
    const g = new THREE.BoxGeometry(1, 1, 1);
    const basket = new THREE.CylinderGeometry(0.13, 0.07, 0.2, 6);
    const flame = new THREE.ConeGeometry(0.09, 0.3, 6);
    const top = wall ? y0 + 2.3 : y0 + 1.75;
    if (wall) kit.add(g, IRON, place(x, top - 0.2, z + 0.1, 0, new THREE.Vector3(0.05, 0.05, 0.3)), undefined, M_PLAIN);
    else {
      kit.add(g, IRON, place(x, y0 + 0.85, z, 0, new THREE.Vector3(0.07, 1.7, 0.07)), undefined, M_PLAIN);
      kit.add(g, IRON, place(x, y0 + 0.05, z, 0.4, new THREE.Vector3(0.45, 0.08, 0.08)), undefined, M_PLAIN);
      kit.add(g, IRON, place(x, y0 + 0.05, z, -0.8, new THREE.Vector3(0.45, 0.08, 0.08)), undefined, M_PLAIN);
    }
    const bz = wall ? z + 0.25 : z;
    kit.add(basket, IRON, place(x, top - 0.1, bz), undefined, M_PLAIN);
    lit.add(flame, '#ffb27a', place(x, top + 0.1, bz));
    this.torchAt.push(new THREE.Vector3(x, top + 0.15, bz));
    g.dispose();
    basket.dispose();
    flame.dispose();
  }

  private buildYard(r: SeventhRules | undefined): void {
    const kit = new Kit({ aMason: 0 });
    const lit = new Kit();
    this.buildKeep(kit, lit, 0, -KH, KH, KN, KS, KEEP_TOP);
    this.buildBastion(kit, lit, 0, 0, -1.5);
    for (const t of YARD_TORCHES) this.buildTorch(kit, lit, t.x, t.z, 0, !!t.wall);
    // The old drains in the flagstones: an iron grate in a ring of stone, black below.
    const L = yardLayout();
    const ring = new THREE.TorusGeometry(0.5, 0.09, 5, 16).rotateX(Math.PI / 2);
    const disc = new THREE.CircleGeometry(0.48, 16).rotateX(-Math.PI / 2);
    const g = new THREE.BoxGeometry(1, 1, 1);
    for (const h of r?.rubble?.holes ?? L.holes) {
      const x = h.x * K;
      const z = h.y * K;
      kit.add(ring, DARK[3] as string, place(x, 0.03, z), undefined, M_PLAIN);
      kit.add(disc, '#030405', place(x, 0.02, z), undefined, M_PLAIN);
      for (let i = -2; i <= 2; i++) kit.add(g, IRON, place(x + i * 0.17, 0.04, z, 0, new THREE.Vector3(0.04, 0.03, 0.86 - Math.abs(i) * 0.14)), undefined, M_PLAIN);
      this.anchors.holes.push(new THREE.Vector3(x, 0.05, z));
    }
    ring.dispose();
    disc.dispose();
    g.dispose();
    this.finishStone(kit, lit, 'sev-yard');
    // The floor: one disc far past the walls.
    const floor = new THREE.CircleGeometry(48, 72).rotateX(-Math.PI / 2);
    const fm = floorMat(this.fu, 'yard');
    this.mats.push(fm);
    this.geos.push(floor);
    const fmesh = new THREE.Mesh(floor, fm);
    fmesh.name = 'sev-floor';
    fmesh.receiveShadow = true;
    this.group.add(fmesh);
    this.buildRubble(r);
    this.buildStair();
    this.anchors.stair = new THREE.Vector3(ST.x, 0.05, STAIR_AT.y * K);
  }

  /** The masonry and the lit pieces into the group (one mesh each). */
  private finishStone(kit: Kit, lit: Kit, name: string): void {
    const stone = stoneMat(this.su);
    this.mats.push(stone);
    const mesh = kit.build(stone, name);
    if (mesh) {
      mesh.castShadow = true;
      // (Not on the keep's top: the low morning sun striped its flags with the shadow map's acne.)
      mesh.receiveShadow = this.place !== 'keepTop';
      this.geos.push(mesh.geometry);
      this.group.add(mesh);
    }
    const lm = new THREE.MeshBasicMaterial({ vertexColors: true, toneMapped: false });
    this.mats.push(lm);
    const lmesh = lit.build(lm, `${name}-lit`);
    if (lmesh) {
      this.geos.push(lmesh.geometry);
      this.group.add(lmesh);
    }
  }

  /**
   * The fall's rubble over the stair's mouth: six heaps of broken blocks, a crenel or two, a charred beam; each shrinks
   * into its foot as it is cleared (one merged mesh, `uProg` per heap).
   */
  private buildRubble(r: SeventhRules | undefined): void {
    const piles = r?.rubble?.piles ?? yardLayout().piles;
    const kit = new Kit({ aMason: 0, aPile: -1, aOrd: 0, aOx: 0, aOy: 0, aOz: 0 });
    const g = new THREE.BoxGeometry(1, 1, 1);
    const dod = new THREE.DodecahedronGeometry(0.5, 0);
    piles.forEach((p, i) => {
      const cx = p.x * K;
      const cz = p.y * K;
      const rnd = rand(9137 + i * 71);
      // Each piece its own middle and its turn to go (the higher, the sooner).
      const v = (x: number, y: number, z: number, mason = 0): Record<string, number> => ({
        aMason: mason,
        aPile: i,
        aOrd: Math.min(0.88, Math.max(0, (1 - y / 0.8) * 0.78 + rnd() * 0.1)),
        aOx: x,
        aOy: y,
        aOz: z,
      });
      for (let k = 0; k < 15; k++) {
        const a = rnd() * Math.PI * 2;
        const d = Math.sqrt(rnd()) * 0.8;
        const s = 0.2 + rnd() * 0.36;
        const y = Math.max(0, 0.62 * (1 - d / 0.9)) + s * 0.25;
        const col = (rnd() < 0.6 ? DARK : PALE)[Math.floor(rnd() * 4)] as string;
        const geo = rnd() < 0.55 ? g : dod;
        const x = cx + Math.cos(a) * d;
        const z = cz + Math.sin(a) * d;
        kit.add(geo, col, place(x, y, z, rnd() * 6, new THREE.Vector3(s * (1 + rnd() * 0.6), s * 0.75, s), rnd() * 0.5, rnd() * 0.5), undefined, v(x, y, z));
      }
      // A broken merlon, a charred beam across the heap (the beam first off).
      const mx = cx + (rnd() - 0.5) * 0.6;
      const mz = cz + (rnd() - 0.5) * 0.6;
      kit.add(g, DARK[1] as string, place(mx, 0.3, mz, rnd() * 3, new THREE.Vector3(0.45, 0.55, 0.38), 0.3, 0.2), undefined, v(mx, 0.3, mz, 1));
      kit.add(g, '#1c1916', place(cx, 0.55, cz, rnd() * 3, new THREE.Vector3(1.4, 0.13, 0.15), 0, 0.25), undefined, { ...v(cx, 0.55, cz), aOrd: 0.02 });
      this.anchors.piles.push(new THREE.Vector3(cx, 0.05, cz));
    });
    g.dispose();
    dod.dispose();
    const mat = stoneMat(this.su, true, this.prog);
    this.mats.push(mat);
    const mesh = kit.build(mat, 'sev-rubble');
    if (mesh) {
      mesh.castShadow = true;
      this.geos.push(mesh.geometry);
      this.group.add(mesh);
    }
  }

  /**
   * The stair under the keep, shown once the last heap is gone: steps of a pale stone older than the tower going down
   * north under its south face, an eye cut in every step (its iris a faint red), the mouth's walls.
   */
  private buildStair(): void {
    const kit = new Kit({ aMason: 0 });
    const lit = new Kit();
    const hw = ST.half;
    const z1 = ST.z1;
    const steps = 9;
    const g = new THREE.BoxGeometry(1, 1, 1);
    const eye = new THREE.TorusGeometry(0.16, 0.025, 4, 14).rotateX(Math.PI / 2);
    const iris = new THREE.CircleGeometry(0.05, 10).rotateX(-Math.PI / 2);
    for (let i = 0; i < steps; i++) {
      const za = z1 - i * 0.36;
      const zb = za - 0.36;
      const y = -0.24 * (i + 1);
      this.box(kit, -hw, hw, zb, za, y - 0.3, y, PALE[i % 4] as string, M_OLD);
      const ez = (za + zb) / 2;
      const ry = y + 0.005;
      const m = place(0, ry, ez, 0, new THREE.Vector3(1, 1, 0.5));
      kit.add(eye, '#4a4e56', m, undefined, M_PLAIN);
      lit.add(iris, '#c23a2c', place(0, ry + 0.003, ez, 0, new THREE.Vector3(1, 1, 0.75)));
    }
    // The mouth's walls, down into the dark under the face; its floor black past the last step.
    this.box(kit, -hw - 0.3, -hw, KS - 1, z1, -2.6, 0.02, PALE[1] as string, M_OLD);
    this.box(kit, hw, hw + 0.3, KS - 1, z1, -2.6, 0.02, PALE[2] as string, M_OLD);
    this.box(kit, -hw, hw, z1 - 0.02, z1 + 0.02, -0.4, 0.0, PALE[0] as string, M_OLD);
    this.box(kit, -hw, hw, KS - 1.2, KS - 1.1, -2.6, 0, '#06070a', M_PLAIN);
    this.box(kit, -hw, hw, KS - 1.2, z1 - steps * 0.36, -2.6, -2.5, '#06070a', M_PLAIN);
    g.dispose();
    eye.dispose();
    iris.dispose();
    const grp = new THREE.Group();
    grp.name = 'sev-stair';
    const stone = stoneMat(this.su);
    this.mats.push(stone);
    const mesh = kit.build(stone, 'sev-stair-stone');
    if (mesh) {
      this.geos.push(mesh.geometry);
      grp.add(mesh);
    }
    const lm = new THREE.MeshBasicMaterial({ vertexColors: true, toneMapped: false });
    this.mats.push(lm);
    const lmesh = lit.build(lm, 'sev-stair-eyes');
    if (lmesh) {
      this.geos.push(lmesh.geometry);
      grp.add(lmesh);
    }
    grp.visible = false;
    this.stair = grp;
    this.group.add(grp);
  }

  /* ---------- the cistern ---------- */

  private buildCistern(r: SeventhRules | undefined): void {
    const kit = new Kit({ aMason: 0 });
    const lit = new Kit();
    const g = new THREE.BoxGeometry(1, 1, 1);
    const cyl = new THREE.CylinderGeometry(1, 1, 1, 14);
    // The outer wall: great blocks of the pale old stone, low on the south (the camera looks over it), rising to an arcade
    // on the north; half-columns between its bays.
    const N = 48;
    for (let i = 0; i < N; i++) {
      const a0 = (i / N) * Math.PI * 2;
      const a1 = ((i + 1) / N) * Math.PI * 2;
      const am = (a0 + a1) / 2;
      const sx = Math.cos(am);
      const sz = Math.sin(am);
      // (The stair's gap at the south.)
      if (sz > 0 && Math.abs(sx * (RR + 0.6)) < SH + 0.3) continue;
      const north = smooth(0.1, -0.7, sz);
      const h = 1.1 + north * 3.6;
      const len = (RR + 0.6) * (a1 - a0) + 0.06;
      kit.add(g, PALE[i % 4] as string, place(sx * (RR + 0.6), h / 2, sz * (RR + 0.6), -am + Math.PI / 2 + Math.PI / 2, new THREE.Vector3(1.2, h, len)), undefined, M_OLD);
      if (i % 4 === 0 && north > 0.2) {
        kit.add(cyl, PALE[3] as string, place(sx * (RR + 0.05), (h + 0.6) / 2, sz * (RR + 0.05), 0, new THREE.Vector3(0.34, h + 0.6, 0.34)), undefined, M_OLD);
        kit.add(g, PALE[2] as string, place(sx * (RR + 0.05), h + 0.4, sz * (RR + 0.05), -am, new THREE.Vector3(0.55, 0.3, 0.55)), undefined, M_OLD);
      } else if (north > 0.2 && i % 2 === 0) {
        // Dark arched bays between the columns (the vault's feet), shallow.
        kit.add(g, '#0a0c10', place(sx * (RR - 0.03), h * 0.42, sz * (RR - 0.03), -am + Math.PI, new THREE.Vector3(0.06, h * 0.72, len * 0.62)), undefined, M_PLAIN);
      }
    }
    // The coping all round and the sill of the lip; the island's curb.
    this.ringOf(kit, BR, 0.32, -0.02, 0.07, 64);
    this.ringOf(kit, IR - 0.05, 0.26, ISLE_Y - 0.62, ISLE_Y + 0.08, 40);
    // The sills on the axes, island to bank.
    for (let k = 0; k < 4; k++) {
      const a = (k / 4) * Math.PI * 2;
      const dx = Math.cos(a);
      const dz = Math.sin(a);
      const r0 = IR + 0.12;
      const r1 = BR - BANK;
      const segs = 6;
      for (let j = 0; j < segs; j++) {
        const ra = r0 + ((r1 - r0) * j) / segs;
        const rb = r0 + ((r1 - r0) * (j + 1)) / segs;
        const rm = (ra + rb) / 2;
        const tilt = Math.atan2(coneY(rb) - coneY(ra), rb - ra);
        kit.add(g, PALE[(j + k) % 4] as string, place(dx * rm, coneY(rm) + SILL / 2 - 0.08, dz * rm, Math.atan2(dx, dz), new THREE.Vector3(SILL_W * 2, SILL + 0.16, rb - ra + 0.02), -tilt), undefined, M_OLD);
      }
    }
    // The stair coming down at the south: steps of the old stone between two walls, rising out of the board.
    for (let i = 0; i < 14; i++) {
      const za = RR - 0.3 + i * 0.42;
      const y = (i + 1) * 0.26;
      this.box(kit, -SH, SH, za, za + 0.42, y - 0.5, y, PALE[i % 4] as string, M_OLD);
    }
    for (const sgn of [-1, 1]) this.box(kit, sgn * SH, sgn * (SH + 0.45), RR - 0.4, RR + 6.2, 0, 4.4, PALE[2] as string, M_OLD);
    // The island: the seventh altar, its stele and the great eye; seven small eyes round the curb.
    this.buildAltar(kit, lit);
    // The gates: their frames (stone posts and lintel), the culverts' mouths in the outer wall, the grates over the channel.
    GATE_DIR.forEach(([dx, dz], i) => {
      const yaw = Math.atan2(dx, dz);
      const px = -dz;
      const pz = dx;
      for (const sgn of [-1, 1]) kit.add(g, PALE[i % 4] as string, place(dx * GR + px * 0.5 * sgn, 0.85, dz * GR + pz * 0.5 * sgn, yaw, new THREE.Vector3(0.32, 1.7, 0.4)), undefined, M_OLD);
      kit.add(g, PALE[(i + 2) % 4] as string, place(dx * GR, 1.78, dz * GR, yaw, new THREE.Vector3(1.38, 0.26, 0.48)), undefined, M_OLD);
      kit.add(g, '#050608', place(dx * (RR - 0.02), 0.32, dz * (RR - 0.02), yaw, new THREE.Vector3(0.56, 0.64, 0.06)), undefined, M_PLAIN);
      for (let k = 0; k < 6; k++) {
        const rr = GR + 0.35 + k * 0.32;
        kit.add(g, IRON, place(dx * rr, 0.012, dz * rr, yaw, new THREE.Vector3(0.62, 0.03, 0.05)), undefined, M_PLAIN);
      }
      this.anchors.gates.push(new THREE.Vector3(dx * (GR + 0.5), 0.05, dz * (GR + 0.5)));
    });
    // The braziers on the rim (west, north, east) and by the stair's foot.
    const bowl = new THREE.CylinderGeometry(0.28, 0.14, 0.22, 8);
    const flame = new THREE.ConeGeometry(0.18, 0.32, 7);
    for (const [x, z] of [
      [-(RR - 0.9), 0],
      [0, -(RR - 0.9)],
      [RR - 0.9, 0],
      [-(SH + 0.9), RR - 0.7],
      [SH + 0.9, RR - 0.7],
    ] as const) {
      for (let k = 0; k < 3; k++) {
        const a = (k / 3) * Math.PI * 2;
        kit.add(g, IRON, place(x + Math.cos(a) * 0.12, 0.42, z + Math.sin(a) * 0.12, -a, new THREE.Vector3(0.04, 0.86, 0.04), 0, 0.18), undefined, M_PLAIN);
      }
      kit.add(bowl, IRON, place(x, 0.86, z), undefined, M_PLAIN);
      lit.add(flame, '#ffb07a', place(x, 1.08, z));
      this.braziers.push(new THREE.Vector3(x, 1.1, z));
    }
    g.dispose();
    cyl.dispose();
    bowl.dispose();
    flame.dispose();
    this.finishStone(kit, lit, 'sev-cistern');
    // The floor: one polar mesh, fine where it bends (the island's bank, the lip's bank).
    const radii: number[] = [0, 0.5, 1.0, 1.4];
    for (let x = IR - 0.3; x <= IR + 0.3; x += 0.06) radii.push(x);
    for (let x = IR + 0.45; x < BR - BANK; x += 0.3) radii.push(x);
    for (let x = BR - BANK; x <= BR + 0.001; x += 0.045) radii.push(x);
    for (let x = BR + 0.2; x <= RR + 1.4; x += 0.6) radii.push(x);
    const floor = polarDisc(radii, this.low ? 64 : 96, (x, z) => cistGround(x, z));
    const fm = floorMat(this.fu, 'cistern');
    this.mats.push(fm);
    this.geos.push(floor);
    const fmesh = new THREE.Mesh(floor, fm);
    fmesh.name = 'sev-floor';
    fmesh.receiveShadow = true;
    this.group.add(fmesh);
    this.buildWater();
    this.buildRunnels(r);
    this.buildGates();
    this.buildShaft();
    this.buildLines();
    this.anchors.altar = new THREE.Vector3(0, ISLE_Y + 0.05, 0);
    this.anchors.island = new THREE.Vector3(0, ISLE_Y + 0.05, 0);
    this.anchors.stair = new THREE.Vector3(0, 0.05, RR - 0.6);
  }

  /** A ring of blocks round the middle at radius r: their width, foot and top (the coping, the island's curb). */
  private ringOf(kit: Kit, r: number, w: number, y0: number, y1: number, n: number): void {
    const g = new THREE.BoxGeometry(1, 1, 1);
    for (let i = 0; i < n; i++) {
      const a = ((i + 0.5) / n) * Math.PI * 2;
      const len = r * ((Math.PI * 2) / n) + 0.04;
      kit.add(g, PALE[i % 4] as string, place(Math.cos(a) * r, (y0 + y1) / 2, Math.sin(a) * r, -a, new THREE.Vector3(w, y1 - y0, len)), undefined, M_OLD);
    }
    g.dispose();
  }

  /**
   * The seventh altar on its island: a low altar of the old stone, behind it a tall stele with the great eye cut in its
   * south face (shut: a seam; open: the white and the red iris, lit); seven small eyes round the island's curb, lit one by
   * one as the oath fills.
   */
  private buildAltar(kit: Kit, lit: Kit): void {
    const g = new THREE.BoxGeometry(1, 1, 1);
    const half = new THREE.CylinderGeometry(0.5, 0.5, 1, 14, 1, false, -Math.PI / 2, Math.PI);
    const y = ISLE_Y;
    // Two steps up to it, the altar, the stele.
    this.box(kit, -1.0, 1.0, -1.15, 0.65, y, y + 0.12, PALE[0] as string, M_OLD);
    this.box(kit, -0.75, 0.75, -1.0, 0.4, y + 0.12, y + 0.24, PALE[2] as string, M_OLD);
    this.box(kit, -0.62, 0.62, -0.42, 0.22, y + 0.24, y + 0.95, PALE[3] as string, M_OLD);
    this.box(kit, -0.7, 0.7, -0.48, 0.28, y + 0.95, y + 1.05, PALE[1] as string, M_OLD);
    this.box(kit, -0.55, 0.55, -0.95, -0.6, y + 0.24, y + 2.3, PALE[0] as string, M_OLD);
    kit.add(half, PALE[0] as string, place(0, y + 2.3, -0.775, 0, new THREE.Vector3(1.1, 0.35, 1.1), Math.PI / 2), undefined, M_OLD);
    // The eye's almond cut round where it opens.
    const ez = -0.595;
    const ey = y + 1.75;
    const almond = new THREE.TorusGeometry(0.3, 0.04, 4, 20);
    kit.add(almond, '#3a3e46', place(0, ey, ez, 0, new THREE.Vector3(1, 0.52, 1)), undefined, M_PLAIN);
    almond.dispose();
    const white = new THREE.Mesh(new THREE.CircleGeometry(0.27, 20), new THREE.MeshBasicMaterial({ color: '#eef2fa', toneMapped: false }));
    white.position.set(0, ey, ez + 0.012);
    // The iris in dragon red, its pupil dark (one disc, drawn by its colours: a ring of red round the black).
    const irisGeo = new THREE.RingGeometry(0.035, 0.12, 20);
    const iris = new THREE.Mesh(irisGeo, new THREE.MeshBasicMaterial({ color: '#ff4f3a', toneMapped: false }));
    iris.position.set(0, ey, ez + 0.02);
    const lid = new THREE.Mesh(new THREE.BoxGeometry(0.6, 0.03, 0.02), new THREE.MeshBasicMaterial({ color: '#1a1c22' }));
    lid.position.set(0, ey, ez + 0.012);
    for (const m of [white, iris, lid]) {
      this.mats.push(m.material as THREE.Material);
      this.geos.push(m.geometry);
      this.group.add(m);
    }
    white.scale.y = 0.02;
    iris.scale.y = 0.02;
    this.eye = { lid, white, iris, at: new THREE.Vector3(0, ey, ez + 0.08), open: 0, want: 0 };
    // Seven small eyes round the curb (the Way's seven), facing out.
    const small = new THREE.TorusGeometry(0.11, 0.02, 4, 12);
    for (let i = 0; i < 7; i++) {
      const a = Math.PI / 2 + ((i - 3) / 7) * Math.PI * 2;
      const x = Math.cos(a) * (IR - 0.04);
      const z = Math.sin(a) * (IR - 0.04);
      kit.add(small, '#2e3238', place(x, y - 0.22, z, -a + Math.PI / 2, new THREE.Vector3(1, 0.55, 1)), undefined, M_PLAIN);
      lit.add(new THREE.CircleGeometry(0.04, 8), '#ff6a50', place(x * 1.004, y - 0.22, z * 1.004, -a + Math.PI / 2));
      this.eyesSeven.push(new THREE.Vector3(x * 1.03, y - 0.22, z * 1.03));
    }
    small.dispose();
    g.dispose();
    half.dispose();
  }

  private buildWater(): void {
    // Four quarters, each its own vertices (no shared seam): a polar patch from the middle to the lip.
    const pos: number[] = [];
    const q: number[] = [];
    const idx: number[] = [];
    const rings = [0, 0.8, IR - 0.2, IR + 0.4, 3, 4, 5, 6, 6.8, BR + 0.02];
    const segs = 12;
    // Quarters (sim): 0 x<0,y≥0; 1 x<0,y<0; 2 x≥0,y<0; 3 x≥0,y≥0 — as angles in (x, z).
    const from = [Math.PI / 2, Math.PI, Math.PI * 1.5, 0];
    for (let k = 0; k < 4; k++) {
      const base = pos.length / 3;
      for (let i = 0; i < rings.length; i++)
        for (let j = 0; j <= segs; j++) {
          const a = (from[k] as number) + (j / segs) * (Math.PI / 2);
          const rr = rings[i] as number;
          pos.push(Math.cos(a) * rr, 0, Math.sin(a) * rr);
          q.push(k);
        }
      const W = segs + 1;
      for (let i = 0; i < rings.length - 1; i++)
        for (let j = 0; j < segs; j++) {
          const a = base + i * W + j;
          const b = a + W;
          idx.push(a, a + 1, b, a + 1, b + 1, b);
        }
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    geo.setAttribute('aQ', new THREE.Float32BufferAttribute(q, 1));
    geo.setIndex(idx);
    const mat = waterMat();
    this.mats.push(mat);
    this.geos.push(geo);
    const mesh = new THREE.Mesh(geo, mat);
    mesh.name = 'sev-water';
    mesh.frustumCulled = false;
    mesh.renderOrder = 2;
    this.group.add(mesh);
    this.water = { mesh, mat, shown: [-10, -10, -10, -10], want: [markY(10), markY(10), markY(10), markY(10)] };
  }

  /** The eight salt runnels: a dark groove between two curbs on the floor; white with salt (one mesh, `uSalt` each). */
  private buildRunnels(r: SeventhRules | undefined): void {
    const list = r?.fled?.runnels ?? cisternLayout(1).runnels;
    const parts: { pts: THREE.Vector3[]; w: number; v: number }[] = [];
    list.forEach((seg, i) => {
      const [x0, y0, x1, y1] = seg.map((v) => v * K) as [number, number, number, number];
      const pts: THREE.Vector3[] = [];
      const curbs: [THREE.Vector3[], THREE.Vector3[]] = [[], []];
      const dx = x1 - x0;
      const dz = y1 - y0;
      const l = Math.hypot(dx, dz) || 1;
      const nx = -dz / l;
      const nz = dx / l;
      for (let k = 0; k <= 12; k++) {
        const t = k / 12;
        const x = x0 + dx * t;
        const z = y0 + dz * t;
        pts.push(new THREE.Vector3(x, cistGround(x, z) + 0.022, z));
        for (const [j, sgn] of [
          [0, -1],
          [1, 1],
        ] as const)
          curbs[j].push(new THREE.Vector3(x + nx * 0.27 * sgn, cistGround(x + nx * 0.27 * sgn, z + nz * 0.27 * sgn) + 0.03, z + nz * 0.27 * sgn));
      }
      parts.push({ pts, w: 0.34, v: i });
      parts.push({ pts: curbs[0], w: 0.1, v: -1 });
      parts.push({ pts: curbs[1], w: 0.1, v: -1 });
      this.anchors.runnels.push(new THREE.Vector3((x0 + x1) / 2, cistGround((x0 + x1) / 2, (y0 + y1) / 2) + 0.05, (y0 + y1) / 2));
    });
    const geo = ribbons(parts, 'aRun');
    // Colour per vertex: the groove dark, the curbs pale.
    const v = geo.getAttribute('aRun') as THREE.BufferAttribute;
    const col = new Float32Array(v.count * 3);
    for (let i = 0; i < v.count; i++) {
      const c = v.getX(i) < -0.5 ? [0.6, 0.62, 0.65] : [0.2, 0.21, 0.23];
      col.set(c, i * 3);
    }
    geo.setAttribute('color', new THREE.BufferAttribute(col, 3));
    const mat = runnelMat(this.salt, this.su);
    mat.polygonOffset = true;
    mat.polygonOffsetFactor = -2;
    mat.polygonOffsetUnits = -2;
    this.mats.push(mat);
    this.geos.push(geo);
    const mesh = new THREE.Mesh(geo, mat);
    mesh.name = 'sev-runnels';
    mesh.renderOrder = 3;
    this.group.add(mesh);
  }

  /** The gates' leaves and wheels (instanced, four each) and the channels' running water (one ribbon mesh). */
  private buildGates(): void {
    const leafGeo = new THREE.BoxGeometry(0.66, 1.0, 0.07);
    const leafMat = new THREE.MeshStandardMaterial({ color: '#30353d', roughness: 0.55, metalness: 0.55, flatShading: true });
    const leaves = new THREE.InstancedMesh(leafGeo, leafMat, 4);
    leaves.name = 'sev-leaves';
    const wk = new Kit();
    const ring = new THREE.TorusGeometry(0.42, 0.04, 5, 18).rotateX(Math.PI / 2);
    const box = new THREE.BoxGeometry(1, 1, 1);
    wk.add(ring, '#3a3f47', new THREE.Matrix4());
    for (let i = 0; i < 3; i++) wk.add(box, '#4a3f37', place(0, 0, 0, (i / 3) * Math.PI, new THREE.Vector3(0.84, 0.04, 0.06)));
    for (let i = 0; i < 6; i++) {
      const a = (i / 6) * Math.PI * 2;
      wk.add(box, '#4a3f37', place(Math.cos(a) * 0.5, 0, Math.sin(a) * 0.5, -a, new THREE.Vector3(0.16, 0.05, 0.05)));
    }
    const axle = new THREE.CylinderGeometry(0.06, 0.06, 0.3, 8);
    wk.add(axle, IRON, place(0, -0.1, 0));
    axle.dispose();
    ring.dispose();
    box.dispose();
    const wm = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.6, metalness: 0.4, flatShading: true });
    const wmesh = wk.build(wm, 'sev-wheel');
    const wheels = new THREE.InstancedMesh(wmesh?.geometry ?? new THREE.BufferGeometry(), wm, 4);
    wheels.name = 'sev-wheels';
    this.mats.push(leafMat, wm);
    this.geos.push(leafGeo, wheels.geometry);
    this.group.add(leaves, wheels);
    this.leaves = leaves;
    this.wheels = wheels;
    this.writeGates();
    // The channels' water: from the lip out under the wall.
    const parts = GATE_DIR.map(([dx, dz], i) => {
      const pts: THREE.Vector3[] = [];
      for (let k = 0; k <= 8; k++) {
        const r = BR - 0.15 + ((RR + 0.4 - (BR - 0.15)) * k) / 8;
        pts.push(new THREE.Vector3(dx * r, 0.018, dz * r));
      }
      return { pts, w: 0.46, v: i };
    });
    const geo = ribbons(parts, 'aSlot');
    const mat = flowMat();
    this.mats.push(mat);
    this.geos.push(geo);
    const mesh = new THREE.Mesh(geo, mat);
    mesh.name = 'sev-channels';
    mesh.renderOrder = 3;
    this.group.add(mesh);
    this.flow = { mat, f: [0, 0, 0, 0] };
  }

  private m4 = new THREE.Matrix4();
  private q4 = new THREE.Quaternion();
  private v3 = new THREE.Vector3();
  private s3 = new THREE.Vector3();
  private e3 = new THREE.Euler();
  private c3 = new THREE.Color();
  private yAxis = new THREE.Vector3(0, 1, 0);

  /** The leaves up as their gates run (shaking when she comes for one), the wheels turning. */
  private writeGates(): void {
    const leaves = this.leaves;
    const wheels = this.wheels;
    if (!leaves || !wheels) return;
    GATE_DIR.forEach(([dx, dz], i) => {
      const yaw = Math.atan2(dx, dz);
      const shake = this.shakeUntil[i] as number > this.now ? Math.sin(this.now * 60 + i) * 0.04 : 0;
      this.q4.setFromAxisAngle(this.yAxis, yaw + shake * 0.5);
      this.m4.compose(this.v3.set(dx * GR + shake, 0.5 + (this.gateUp[i] as number) * 0.85, dz * GR), this.q4, this.s3.set(1, 1, 1));
      leaves.setMatrixAt(i, this.m4);
      this.e3.set(0, this.gateAng[i] as number, 0);
      this.q4.setFromEuler(this.e3);
      this.m4.compose(this.v3.set(dx * GR, 2.02, dz * GR), this.q4, this.s3.set(1, 1, 1));
      wheels.setMatrixAt(i, this.m4);
    });
    leaves.instanceMatrix.needsUpdate = true;
    wheels.instanceMatrix.needsUpdate = true;
  }

  private buildShaft(): void {
    const geo = new THREE.CylinderGeometry(1.5, 2.4, 8, 20, 1, true);
    geo.translate(0, 4 + ISLE_Y, 0);
    const mat = shaftMat();
    this.mats.push(mat);
    this.geos.push(geo);
    const mesh = new THREE.Mesh(geo, mat);
    mesh.name = 'sev-shaft';
    mesh.renderOrder = 8;
    mesh.frustumCulled = false;
    this.group.add(mesh);
    this.shaft = mat;
  }

  /** Her two lines (hers thick, her reflection's thin): a ribbon each, rewritten only when the line moves. */
  private buildLines(): void {
    for (const thick of [true, false]) {
      const n = 17;
      const geo = new THREE.BufferGeometry();
      geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(n * 2 * 3), 3).setUsage(THREE.DynamicDrawUsage));
      const uv = new Float32Array(n * 2 * 2);
      const idx: number[] = [];
      for (let i = 0; i < n; i++) {
        uv.set([0, i / (n - 1), 1, i / (n - 1)], i * 4);
        if (i > 0) {
          const k = i * 2;
          idx.push(k - 2, k - 1, k, k - 1, k + 1, k);
        }
      }
      geo.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
      geo.setIndex(idx);
      const mat = lineMat(thick);
      this.mats.push(mat);
      this.geos.push(geo);
      const mesh = new THREE.Mesh(geo, mat);
      mesh.name = thick ? 'sev-line' : 'sev-echo';
      mesh.frustumCulled = false;
      mesh.renderOrder = 6;
      mesh.visible = false;
      this.group.add(mesh);
      this.lines.push({ mesh, mat, key: '', show: 0 });
    }
  }

  /** A line's ribbon from (x0, y0) to (x1, y1) (sim), over the floor or the water there. */
  private writeLine(l: LineRib, seg: readonly number[], w: SeventhState, r: SeventhRules, width: number): void {
    const key = seg.join(',');
    if (key === l.key) return;
    l.key = key;
    const pos = l.mesh.geometry.getAttribute('position') as THREE.BufferAttribute;
    const n = pos.count / 2;
    const x0 = (seg[0] as number) * K;
    const z0 = (seg[1] as number) * K;
    const x1 = (seg[2] as number) * K;
    const z1 = (seg[3] as number) * K;
    const dx = x1 - x0;
    const dz = z1 - z0;
    const len = Math.hypot(dx, dz) || 1;
    const nx = (-dz / len) * width * 0.5;
    const nz = (dx / len) * width * 0.5;
    for (let i = 0; i < n; i++) {
      const t = i / (n - 1);
      const x = x0 + dx * t;
      const z = z0 + dz * t;
      const wet = sevWet(r, w, Math.round(x / K), Math.round(z / K));
      const q = x < 0 ? (z >= 0 ? 0 : 1) : z < 0 ? 2 : 3;
      const lv = r.kind === 'fled' ? markY(w.quarter[q] ?? 0) : markY(w.level);
      const y = Math.max(cistGround(x, z), wet ? lv : -9) + 0.04;
      pos.setXYZ(i * 2, x + nx, y, z + nz);
      pos.setXYZ(i * 2 + 1, x - nx, y, z - nz);
    }
    pos.needsUpdate = true;
    l.mesh.geometry.computeBoundingSphere();
  }

  /* ---------- the top of the keep ---------- */

  /**
   * The epilogue: the keep's top in the morning, a terrace of flags inside its parapet and corner turrets, the hatch and
   * the Way's banner on its pole; far below the yard (the bastion's walls round it, the barracks), the salt folk and the
   * knights at their bread at a long table, the monastery's wheel beside it; the Way going out of the gate down into
   * the hills.
   */
  private buildTop(): void {
    const kit = new Kit({ aMason: 0 });
    const lit = new Kit();
    const g = new THREE.BoxGeometry(1, 1, 1);
    const y0 = -TOP_DROP;
    // The terrace: its north edge just past where Marit stands (the scene's camera looks over it, down into the yard),
    // its flags; the keep's body under it, its turrets rising at the corners.
    const X = 5.6;
    const N = -1.25;
    const S2 = 7.6;
    this.buildKeep(kit, lit, y0, -X, X, N, S2, TOP_DROP - 0.55, false);
    this.box(kit, -X, X, N, S2, -0.6, 0, DARK[3] as string, M_ASHLAR);
    // The parapet on three sides; on the north, two short runs either side of a wide gap at the edge (where she looks out).
    const wall = (x0: number, x1: number, z0: number, z1: number): void => {
      this.box(kit, x0, x1, z0, z1, 0, 0.75, DARK[2] as string);
      const alongX = x1 - x0 > z1 - z0;
      const m = alongX ? (z0 + z1) / 2 : (x0 + x1) / 2;
      if (alongX) this.crenels(kit, x0, m, x1, m, 0.75, DARK[0] as string, 0.42);
      else this.crenels(kit, m, z0, m, z1, 0.75, DARK[0] as string, 0.42);
    };
    wall(-X - 0.36, X + 0.36, S2, S2 + 0.36);
    wall(-X - 0.36, -X, N - 0.36, S2);
    wall(X, X + 0.36, N - 0.36, S2);
    wall(-X - 0.36, -1.9, N - 0.36, N);
    wall(1.9, X + 0.36, N - 0.36, N);
    // A low kerb of stone at the gap's lip.
    this.box(kit, -1.9, 1.9, N - 0.3, N, 0, 0.12, PALE[1] as string, M_ASHLAR);
    // The hatch and its little house at the south-east; the banner's pole at the north-west, the Way's banner on it.
    this.box(kit, 3.4, 4.9, 5.0, 6.5, 0, 1.6, DARK[1] as string);
    this.box(kit, 3.3, 5.0, 4.9, 6.6, 1.6, 1.75, SLATE, M_PLAIN);
    this.box(kit, 3.7, 4.5, 4.98, 5.02, 0, 1.25, WOOD, M_PLAIN);
    kit.add(g, IRON, place(-4.6, 2.3, -0.2, 0, new THREE.Vector3(0.08, 4.6, 0.08)), undefined, M_PLAIN);
    wayBanner(kit, place(-4.6, 3.9, -0.18, Math.PI * 0.2, 1.2));
    // The yard far below (the keep at its north side; the gate to the north, the Way out of it), its walls, its flags.
    this.buildBastion(kit, lit, y0, 0, -4, -Math.PI / 2);
    // (Only inside the walls: beyond them the land falls away.)
    const floor = new THREE.CircleGeometry(WALL_R + 0.8, 72).rotateX(-Math.PI / 2).translate(0, y0 + 0.02, -4);
    const fm = floorMat(this.fu, 'yard');
    this.mats.push(fm);
    this.geos.push(floor);
    const fmesh = new THREE.Mesh(floor, fm);
    fmesh.name = 'sev-top-yard';
    this.group.add(fmesh);
    // The bread table to the north under the keep: the salt folk and the two knights about it, the wheel leaning by.
    // (Drawn larger than life, `BIG`: from the keep's top, nine metres up and twenty off, people at their true size are
    // specks on a phone; the scene's second camera looks down on them.)
    const BIG = 1.75;
    const tx = -2.2;
    const tz = -16.5;
    const tb = (x0: number, x1: number, z0: number, z1: number, h0: number, h1: number, c: string): void => this.box(kit, tx + x0 * BIG, tx + x1 * BIG, tz + z0 * BIG, tz + z1 * BIG, y0 + h0 * BIG, y0 + h1 * BIG, c, M_PLAIN);
    tb(-1.8, 1.8, -0.4, 0.4, 0.62, 0.7, WOOD);
    for (const sx of [-1.5, 1.5]) tb(sx - 0.05, sx + 0.05, -0.35, 0.35, 0, 0.62, WOOD);
    // The benches either side.
    for (const sz of [-0.95, 0.95]) tb(-1.7, 1.7, sz - 0.14, sz + 0.14, 0.36, 0.42, WOOD);
    // A white cloth down the board's middle, the loaves on it.
    tb(-1.6, 1.6, -0.22, 0.22, 0.7, 0.715, '#dfe4ea');
    const loaf = new THREE.SphereGeometry(0.14 * BIG, 7, 5);
    for (let i = 0; i < 5; i++) kit.add(loaf, '#8a6e56', place(tx + (-1.2 + i * 0.6) * BIG, y0 + 0.78 * BIG, tz + (i % 2 ? 0.12 : -0.1) * BIG, i, new THREE.Vector3(1.3, 0.7, 0.9)), undefined, M_PLAIN);
    loaf.dispose();
    const folk = new Kit();
    const fig = (id: string, x: number, z: number, yaw: number): void => mergeFigure(folk, id, place(tx + x * BIG, y0, tz + z * BIG, yaw, SCALE.hero * 1.05 * BIG));
    fig('salinera', -1.3, 0.95, Math.PI);
    fig('salter', 0.1, 1.0, Math.PI + 0.2);
    fig('salter2', 1.4, 0.95, Math.PI - 0.1);
    fig('orderKnight', -0.7, -1.0, 0);
    fig('orderKnight', 0.9, -1.0, 0.2);
    const fmat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.8, metalness: 0.1 });
    this.mats.push(fmat);
    const fmesh2 = folk.build(fmat, 'sev-top-folk');
    if (fmesh2) {
      this.geos.push(fmesh2.geometry);
      this.group.add(fmesh2);
    }
    damWheel(kit, place(tx + 3.6 * BIG, y0 + 1.08 * BIG, tz - 0.4 * BIG, 0.5, 1.05 * BIG, 0, 0.12), M_PLAIN);
    g.dispose();
    this.finishStone(kit, lit, 'sev-top');
    this.buildLand();
  }

  /** The land beyond the walls: hills falling away, the gorge's cut, far blue mountains; the Way's pale road down them. */
  private buildLand(): void {
    const n = this.low ? 72 : 120;
    const size = 640;
    const geo = new THREE.PlaneGeometry(size, size, n, n).rotateX(-Math.PI / 2);
    const pos = geo.getAttribute('position') as THREE.BufferAttribute;
    const col = new Float32Array(pos.count * 3);
    const c = new THREE.Color();
    const way = (z: number): number => Math.sin(z * 0.045) * 9 + Math.sin(z * 0.11 + 1) * 3;
    const hn = (x: number, z: number): number => Math.sin(x * 0.07 + 1.3) * Math.cos(z * 0.05) * 0.5 + Math.sin(x * 0.021 - z * 0.017) + Math.sin(x * 0.13 + z * 0.11) * 0.25;
    for (let i = 0; i < pos.count; i++) {
      const x = pos.getX(i);
      const z = pos.getZ(i);
      const r = Math.hypot(x, z + 4);
      // The bastion's ground to the walls; the hills dropping below it; the mountains rising far to the north and west.
      const fall = smooth(WALL_R + 2, 90, r);
      let y = -TOP_DROP - fall * 34 + hn(x, z) * 6 * fall;
      y += smooth(200, 70, Math.hypot(x + 90, z + 290)) * 60 * (0.6 + 0.4 * hn(x * 2, z * 2));
      // The Way's road cut along the hills north from the gate (flattened under it).
      const d = Math.abs(x - way(z));
      if (z < -WALL_R - 4) y = y + (1 - smooth(1.5, 5, d)) * -0.6;
      if (r < WALL_R + 1.5) y = -TOP_DROP - 0.05;
      pos.setY(i, y);
      const steep = smooth(0.0, 1.0, fall);
      c.set('#3e4a44').lerp(new THREE.Color('#6d7a80'), smooth(-10, 40, y)).lerp(new THREE.Color('#d7dee8'), smooth(30, 55, y));
      if (z < -WALL_R - 4 && d < 2.2) c.set('#b3b8bd');
      c.multiplyScalar(0.85 + 0.15 * steep);
      col.set([c.r, c.g, c.b], i * 3);
    }
    geo.setAttribute('color', new THREE.BufferAttribute(col, 3));
    geo.computeVertexNormals();
    const mat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.95, metalness: 0, flatShading: true });
    this.mats.push(mat);
    this.geos.push(geo);
    const mesh = new THREE.Mesh(geo, mat);
    mesh.name = 'sev-land';
    mesh.position.z = 0;
    // (The yard's own floor is drawn over its middle.)
    mesh.renderOrder = -1;
    this.group.add(mesh);
  }

  /* ---------- decals ---------- */

  private buildDecals(): void {
    const q = new THREE.PlaneGeometry(1, 1).rotateX(-Math.PI / 2);
    this.geos.push(q);
    const N = 96;
    q.setAttribute('aKind', new THREE.InstancedBufferAttribute(new Float32Array(N), 1));
    q.setAttribute('aAlpha', new THREE.InstancedBufferAttribute(new Float32Array(N), 1));
    const m = decalMat();
    this.mats.push(m);
    const im = new THREE.InstancedMesh(q, m, N);
    im.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(N * 3), 3);
    im.count = 0;
    im.frustumCulled = false;
    im.renderOrder = 5;
    im.name = 'sev-decals';
    this.decals = im;
    this.group.add(im);
  }

  /* ---------- each tick and frame ---------- */

  tick(s: BattleState): void {
    if (s.tick === this.lastTick) return;
    this.lastTick = s.tick;
    for (const e of s._events ?? []) {
      if (e.t !== 'senda') continue;
      const p = this.ctx.at(e.x, e.y, 0.08);
      switch (e.kind) {
        case 'pile':
          this.ctx.vfx.burst(p, { n: 10, color: '#6a6e76', speed: 0.04, up: 0.06, size: 0.1, life: 12, smoke: true });
          this.ctx.vfx.burst(p, { n: 6, color: '#8d9198', speed: 0.05, up: 0.08, size: 0.06, life: 10, gravity: 0.006 });
          break;
        case 'stair':
          this.ctx.shake(0.45);
          this.ctx.vfx.burst(this.ctx.at(e.x, e.y, 0.3), { n: 30, color: '#7a7e86', speed: 0.06, up: 0.08, size: 0.18, life: 22, smoke: true, spread: 1.4 });
          this.ctx.vfx.ring(this.ctx.at(e.x, e.y, 0.05), 0.3, 2.4, '#ff8a6e', 9, 0.7);
          this.flashUntil = this.now + 2.5;
          break;
        case 'gate':
          this.ctx.vfx.burst(p, { n: 7, color: '#9aa2ac', speed: 0.04, up: 0.03, size: 0.05, life: 6 });
          break;
        case 'shake': {
          const g = e.n ?? 0;
          if (g >= 0 && g < 4) this.shakeUntil[g] = this.now + 3;
          break;
        }
        case 'pour':
        case 'spring':
        case 'surface':
          this.ctx.vfx.burst(p.setY(this.waterAt(e.x, e.y) + 0.04), { n: 8, color: '#cfd8ea', speed: 0.035, up: 0.05, size: 0.06, life: 8, spread: 0.3 });
          break;
        case 'salt':
          this.ctx.vfx.burst(p, { n: 14, color: '#f2f5f7', speed: 0.05, up: 0.04, size: 0.06, life: 8, spread: 0.6 });
          break;
        case 'run':
          // Covered (the hero or Varo took it): a white ring where it struck; else on Marit.
          this.ctx.vfx.ring(p.setY(p.y - 0.04), 0.15, e.n === 1 || e.n === 3 ? 1.3 : 0.8, e.n === 1 || e.n === 3 ? '#ffffff' : '#ff8a6e', 7, 0.7);
          break;
        case 'hide':
        case 'fade':
          this.ctx.vfx.implode(this.ctx.at(e.x, e.y, 0.7), 0.7, { n: 12, color: '#dfe6f2', size: 0.08, life: 12, swirl: 1.4, lift: 0.02 });
          break;
        case 'still':
          this.ctx.vfx.ring(p.setY(p.y - 0.04), 0.2, 1.1, '#e8eeff', 8, 0.8);
          break;
        case 'sworn':
          if (this.anchors.altar) this.ctx.vfx.ring(this.anchors.altar.clone().setY(ISLE_Y + 0.05), 0.3, 2.6, '#ffffff', 10, 0.8);
          this.oath = 1;
          break;
        case 'down':
          if (e.npc === 'runaway') this.ctx.vfx.burst(this.ctx.at(e.x, e.y, 0.8), { n: 26, color: '#eef2fa', speed: 0.04, up: 0.06, size: 0.14, life: 22, smoke: true, spread: 0.7 });
          break;
        default:
          break;
      }
    }
  }

  /** The water's height over a sim point (where it stands; under the floor where it doesn't). */
  private waterAt(x: number, y: number): number {
    const q = x < 0 ? (y >= 0 ? 0 : 1) : y < 0 ? 2 : 3;
    const lv = this.water?.want[q] ?? -9;
    return Math.max(lv, cistGround(x * K, y * K));
  }

  draw(s: BattleState): void {
    const r = s.rules?.senda?.seventh;
    const w = s.senda?.seventh;
    // The torches the Order's knights (and, in the speech, the salt folk) carry: a fire at each one's hand.
    this.carried.length = 0;
    const carry = this.place === 'keepNight' || this.place === 'keepFar' ? ['orderKnight', 'orderKnight2'] : this.place === 'cistern7' && this.scene ? ['salinera', 'salter', 'salter2'] : null;
    if (carry) {
      const hold = (x0: number, y0: number, facing: number): void => {
        const a = (facing / 1024) * Math.PI * 2;
        const x = (x0 + Math.cos(a + 1.2) * 260) * K;
        const z = (y0 + Math.sin(a + 1.2) * 260) * K;
        this.carried.push({ x, y: this.ground(x, z) + 1.12, z });
      };
      // (A scene's people are heroes on its board; a fight's are the path's own.)
      for (const h of s.heroes) if (h.pid.startsWith('npc:') && carry.includes(h.pid.slice(4))) hold(h.x, h.y, h.facing);
      for (const n of s.senda?.npcs ?? []) if (carry.includes(n.id) && n.hp > 0) hold(n.x, n.y, 256);
    }
    if (this.place === 'cistern7') this.drawCistern(s, r, w);
    else if (this.place !== 'keepTop') this.drawYard(s, r, w);
  }

  private drawYard(_s: BattleState, r: SeventhRules | undefined, w: SeventhState | undefined): void {
    const R = r?.rubble;
    const P = this.prog.value;
    for (let i = 0; i < 6; i++) P[i] = R && w ? Math.min(1, (w.cleared[i] ?? 0) / Math.max(1, R.stand)) : 0;
    const open = !!(R && w && w.doneAt >= 0);
    if (this.stair) this.stair.visible = open;
    this.fu.uStair.value = open ? 1 : 0;
  }

  private drawCistern(s: BattleState, r: SeventhRules | undefined, w: SeventhState | undefined): void {
    const water = this.water;
    if (water) {
      for (let k = 0; k < 4; k++) {
        let lv = markY(10);
        if (r && w) lv = r.kind === 'fled' ? ((w.quarter[k] ?? 0) > 0 ? markY(w.quarter[k] as number) : -9) : markY(w.level);
        water.want[k] = lv;
      }
    }
    // The gates: running (the test: pulses queued; the fight: open), the leaves up, the wheels turning.
    for (let g = 0; g < 4; g++) {
      let run = 0;
      if (r && w) run = r.kind === 'well' ? ((w.open[g] ?? 0) > 0 && w.level > 0 ? 1 : 0) : r.kind === 'fled' ? ((w.gate[g] ?? 0) > 0 ? 1 : 0) : 0;
      this.gateRun[g] = run;
      if (r?.fled && w && (w.reopen[g] ?? 0) > 0 && s.tick >= (w.reopen[g] as number) - r.fled.gate.warn) this.shakeUntil[g] = Math.max(this.shakeUntil[g] as number, this.now + 0.3);
    }
    // The runnels' salt.
    for (let i = 0; i < 8; i++) this.saltWant[i] = w && (w.salt[i] ?? 0) > 0 ? 1 : 0;
    // The oath's bar: the seven eyes, lit one by one.
    if (r?.fled && w) this.oath = Math.max(this.oath, Math.min(1, w.oath / Math.max(1, r.fled.oath)));
    if (this.eye && r?.fled && w && w.fell >= 0 && w.oath >= r.fled.oath) this.eye.want = 1;
    // Her lines while she marks them.
    if (r && w) {
      const marking = w.act === 1;
      const L0 = this.lines[0];
      const L1 = this.lines[1];
      if (L0 && w.line.length === 4) this.writeLine(L0, w.line, w, r, 0.62);
      if (L1 && w.echo.length === 4) this.writeLine(L1, w.echo, w, r, 0.3);
      if (L0) L0.mesh.userData.want = marking && w.line.length === 4 ? 1 : 0;
      if (L1) L1.mesh.userData.want = marking && w.echo.length === 4 ? 1 : 0;
      const urge = marking ? Math.max(0, Math.min(1, 1 - (w.until - s.tick) / Math.max(1, r.fled?.warn ?? 36))) : 0;
      for (const l of this.lines) (l.mat.uniforms.urge as THREE.IUniform<number>).value = urge;
    }
    this.drawDecals(s, r, w);
  }

  private drawDecals(s: BattleState, r: SeventhRules | undefined, w: SeventhState | undefined): void {
    const d = this.decals;
    if (!d) return;
    const kind = d.geometry.getAttribute('aKind') as THREE.InstancedBufferAttribute;
    const alpha = d.geometry.getAttribute('aAlpha') as THREE.InstancedBufferAttribute;
    const cap = kind.count;
    let n = 0;
    const put = (x: number, z: number, y: number, sx: number, sz: number, yaw: number, k: number, a: number, col: THREE.Color): void => {
      if (n >= cap) return;
      this.q4.setFromAxisAngle(this.yAxis, yaw);
      this.m4.compose(this.v3.set(x, y, z), this.q4, this.s3.set(sx, 1, sz));
      d.setMatrixAt(n, this.m4);
      d.setColorAt(n, col);
      kind.setX(n, k);
      alpha.setX(n, a);
      n++;
    };
    if (r && w && (r.kind === 'well' || r.kind === 'fled')) {
      // Where she (or a squire) walks unseen: rings on the water, prints on the salt; fading over three seconds.
      const life = 36;
      const ring = this.c3.set('#dfe8ff');
      for (let i = Math.max(0, w.marks.length - 90); i + 2 < w.marks.length; i += 3) {
        const age = s.tick - (w.marks[i + 2] as number);
        if (age > life || age < 0) continue;
        const x = w.marks[i] as number;
        const y = w.marks[i + 1] as number;
        const wx = x * K;
        const wz = y * K;
        const k = age / life;
        if (sevWet(r, w, x, y)) put(wx, wz, this.waterAt(x, y) + 0.015, 0.4 + k * 1.3, 0.4 + k * 1.3, 0, D_RIPPLE, 0.75 * (1 - k), ring);
        else {
          const nx = w.marks[i + 3] as number | undefined;
          const ny = w.marks[i + 4] as number | undefined;
          // (Toes along the way she went: the quad's toes are its −z.)
          const yaw = nx !== undefined && ny !== undefined ? Math.atan2(x - nx, y - ny) : 0;
          put(wx, wz, cistGround(wx, wz) + 0.03, 0.17, 0.3, yaw, D_PRINT, 0.9 * (1 - k), this.c3.set('#2e333b'));
        }
      }
      // A breath of white at the altar's foot as the oath fills.
      if (r.fled && this.oathShown > 0.02) put(0, 0.5, ISLE_Y + 0.03, 2.6, 2.6, 0, D_SOFT, 0.18 * this.oathShown, this.c3.set('#e8eeff'));
    }
    d.count = n;
    d.instanceMatrix.needsUpdate = true;
    if (d.instanceColor) d.instanceColor.needsUpdate = true;
    kind.needsUpdate = true;
    alpha.needsUpdate = true;
  }

  frame(now: number, camera?: THREE.Camera): void {
    const dt = this.now > 0 ? Math.min(0.1, now - this.now) : 0;
    this.now = now;
    this.dome?.frame(now, camera);
    this.su.uTime.value = now;
    this.fu.uTime.value = now;
    const dm = this.decals?.material as THREE.ShaderMaterial | undefined;
    if (dm) (dm.uniforms.time as THREE.IUniform<number>).value = now;
    const water = this.water;
    if (water) {
      const lv = water.mat.uniforms.uLevel as THREE.IUniform<THREE.Vector4>;
      // (Slow enough that a mark's rise or fall is watched happening; the ripples quicken and the foam brightens meanwhile.)
      let moving = 0;
      for (let k = 0; k < 4; k++) {
        const want = water.want[k] as number;
        let sh = water.shown[k] as number;
        sh = sh < -5 || want < -5 ? want : sh + Math.sign(want - sh) * Math.min(Math.abs(want - sh), dt * (0.12 + Math.abs(want - sh) * 1.2));
        if (want > -5) moving = Math.max(moving, Math.abs(want - sh));
        water.shown[k] = sh;
      }
      const stir = water.mat.uniforms.uStir as THREE.IUniform<number>;
      stir.value += (Math.min(1, moving / (MARK * 0.5)) - stir.value) * Math.min(1, dt * 4);
      lv.value.set(water.shown[0] as number, water.shown[1] as number, water.shown[2] as number, water.shown[3] as number);
      (water.mat.uniforms.time as THREE.IUniform<number>).value = now;
      const t = this.tremble >= 0 ? now - this.tremble : -1;
      (water.mat.uniforms.uTremble as THREE.IUniform<number>).value = t >= 0 && t < 4 ? Math.sin((t / 4) * Math.PI) : 0;
      (water.mat.uniforms.uTrembleT as THREE.IUniform<number>).value = Math.max(0, t);
    }
    if (this.shaft) {
      (this.shaft.uniforms.time as THREE.IUniform<number>).value = now;
      (this.shaft.uniforms.strength as THREE.IUniform<number>).value = this.shaftK;
    }
    // The gates.
    if (this.leaves) {
      for (let g = 0; g < 4; g++) {
        const run = this.gateRun[g] as number;
        this.gateUp[g] = (this.gateUp[g] as number) + (run - (this.gateUp[g] as number)) * Math.min(1, dt * 3);
        this.gateSpin[g] = (this.gateSpin[g] as number) + ((run ? 2.4 : 0) - (this.gateSpin[g] as number)) * Math.min(1, dt * 2);
        this.gateAng[g] = (this.gateAng[g] as number) + dt * (this.gateSpin[g] as number);
      }
      this.writeGates();
    }
    if (this.flow) {
      const f = this.flow.mat.uniforms.uFlow as THREE.IUniform<THREE.Vector4>;
      for (let g = 0; g < 4; g++) this.flow.f[g] = (this.flow.f[g] as number) + ((this.gateRun[g] as number) - (this.flow.f[g] as number)) * Math.min(1, dt * 2.5);
      f.value.set(this.flow.f[0] as number, this.flow.f[1] as number, this.flow.f[2] as number, this.flow.f[3] as number);
      (this.flow.mat.uniforms.time as THREE.IUniform<number>).value = now;
    }
    const sv = this.salt.value;
    for (let i = 0; i < 8; i++) sv[i] = (sv[i] as number) + ((this.saltWant[i] as number) - (sv[i] as number)) * Math.min(1, dt * 4);
    for (const l of this.lines) {
      const want = (l.mesh.userData.want as number | undefined) ?? 0;
      l.show += (want - l.show) * Math.min(1, dt * 8);
      l.mesh.visible = l.show > 0.02;
      (l.mat.uniforms.show as THREE.IUniform<number>).value = l.show;
      (l.mat.uniforms.time as THREE.IUniform<number>).value = now;
    }
    this.oathShown += (this.oath - this.oathShown) * Math.min(1, dt * 1.5);
    // The great eye opening.
    const eye = this.eye;
    if (eye) {
      eye.open += (eye.want - eye.open) * Math.min(1, dt * 0.9);
      const k = eye.open;
      eye.white.scale.set(0.9, Math.max(0.02, k * 0.48), 1);
      eye.iris.scale.y = Math.max(0.02, Math.min(1, k * 1.1));
      eye.white.visible = k > 0.03;
      eye.iris.visible = k > 0.03;
      eye.lid.visible = k < 0.5;
    }
    if (this.stair) this.stairShown += ((this.stair.visible ? 1 : 0) - this.stairShown) * Math.min(1, dt * 1.2);
    this.fillGlow(now);
  }

  private fillGlow(now: number): void {
    const g = this.glow;
    g.begin();
    for (let i = 0; i < this.torchAt.length; i++) {
      const t = this.torchAt[i] as THREE.Vector3;
      const f = 0.78 + 0.22 * Math.sin(now * (6 + (i % 4)) + i * 1.7) * Math.sin(now * 3.1 + i);
      g.add(t.x, t.y, t.z, FIRE, 2.6, 0.55 * f);
      g.add(t.x, t.y, t.z, FIRE_CORE, 0.6, 0.8 * f);
    }
    for (let i = 0; i < this.braziers.length; i++) {
      const t = this.braziers[i] as THREE.Vector3;
      const f = 0.8 + 0.2 * Math.sin(now * (5 + i) + i * 2.1);
      g.add(t.x, t.y, t.z, FIRE, 3.2, 0.5 * f);
      g.add(t.x, t.y, t.z, FIRE_CORE, 0.8, 0.75 * f);
    }
    for (let i = 0; i < this.carried.length; i++) {
      const t = this.carried[i] as { x: number; y: number; z: number };
      const f = 0.78 + 0.22 * Math.sin(now * (7 + i) + i * 1.3);
      g.add(t.x, t.y, t.z, FIRE, 2.2, 0.55 * f);
      g.add(t.x, t.y, t.z, FIRE_CORE, 0.5, 0.85 * f);
    }
    const night = this.place === 'keepTop' ? 0.15 : 1;
    for (const w of this.windows) g.add(w.x, w.y, w.z, FIRE, 1.2, 0.3 * night);
    // The stair's eyes, faint red, once it is open (a red breath at the mouth as it opens).
    if (this.stairShown > 0.02) {
      const k = this.stairShown;
      for (let i = 0; i < 9; i++) g.add(0, -0.24 * (i + 1) + 0.05, ST.z1 - i * 0.36 - 0.18, RED_SOFT, 0.5, 0.22 * k * (0.8 + 0.2 * Math.sin(now * 2 + i)));
      if (this.flashUntil > now) g.add(0, 0.4, ST.z1 - 1.4, RED_SOFT, 5, 0.35 * ((this.flashUntil - now) / 2.5));
    }
    // The altar: the seven small eyes by the oath, the great one open.
    if (this.eyesSeven.length) {
      const lit = this.oathShown * 7;
      for (let i = 0; i < 7; i++) {
        const k = Math.max(0, Math.min(1, lit - i));
        if (k <= 0.01) continue;
        const e = this.eyesSeven[i] as THREE.Vector3;
        g.add(e.x, e.y, e.z, RED_SOFT, 0.55, 0.55 * k);
        g.add(e.x, e.y, e.z, WHITE, 0.22, 0.7 * k);
      }
    }
    const eye = this.eye;
    if (eye && eye.open > 0.03) {
      const k = eye.open;
      const p = eye.at;
      const pulse = 0.9 + 0.1 * Math.sin(now * 1.6);
      g.add(p.x, p.y, p.z + 0.1, RED, 2.4 * k, 0.4 * k * pulse);
      g.add(p.x, p.y, p.z + 0.1, WHITE, 0.6 * k, 0.3 * k);
      g.add(0, ISLE_Y + 0.4, 0.4, COLD, 4.5, 0.12 * k);
    }
    g.end();
  }

  dispose(): void {
    for (const m of this.mats) m.dispose();
    for (const g of this.geos) g.dispose();
    this.dome?.dispose();
    this.glow.mesh.geometry.dispose();
    this.glow.material.dispose();
    this.group.removeFromParent();
  }
}

/**
 * The roots of the keep (act 2, a crypt's descent): the rooms re-dressed in a pale stone far older than the fortress,
 * smooth and worn, and eyes cut in their walls all along: each lights (a red iris and a white breath, glows) as a
 * paladin comes near, and stays lit, so the way behind is a line of open eyes. The crypt's own flames and runes are put
 * out; its room lights turned cold and low.
 *
 * Draw calls: the dressing (one merged mesh), the glows (one). No lights of its own.
 */
export class RootsCrypt {
  readonly group = new THREE.Group();
  private mats: THREE.Material[] = [];
  private geos: THREE.BufferGeometry[] = [];
  private glow = new GlowLayer(260);
  private eyes: { p: THREE.Vector3; x: number; y: number; lit: number }[] = [];
  private now = 0;

  constructor(
    private ctx: OmenCtx,
    map: MapDef,
    crypt: { mesh: THREE.Mesh; runes: THREE.Group; flames: { mesh: THREE.Object3D; glow: THREE.Sprite }[]; decor: THREE.Group },
    rooms: THREE.PointLight[],
  ) {
    this.group.name = 'roots';
    crypt.runes.visible = false;
    for (const f of crypt.flames) {
      f.mesh.visible = false;
      f.glow.visible = false;
    }
    rooms.forEach((l) => {
      l.color.set(0xb8c8ec);
      l.intensity *= 0.7;
    });
    // The old pale stone: the floor worn smooth, the walls in great blocks, their tops paler.
    const geo = crypt.mesh.geometry;
    const col = geo.getAttribute('color') as THREE.BufferAttribute | undefined;
    const pos = geo.getAttribute('position') as THREE.BufferAttribute | undefined;
    if (col && pos) {
      const c = new THREE.Color();
      for (let i = 0; i < pos.count; i++) {
        const x = pos.getX(i);
        const y = pos.getY(i);
        const z = pos.getZ(i);
        const lum = col.getX(i) * 0.3 + col.getY(i) * 0.59 + col.getZ(i) * 0.11;
        const n = 0.5 + 0.5 * Math.sin(x * 1.7 + z * 1.1) * Math.cos(z * 1.3 - x * 0.6);
        if (lum < 0.04) c.set('#14161b');
        else if (y > 0.5) c.set(PALE[Math.floor(n * 3.99)] as string).multiplyScalar(0.95);
        else if (y > 0.05) c.set(PALE[Math.floor(n * 3.99)] as string).multiplyScalar(0.62 + lum * 0.5);
        else c.set('#7c8189').multiplyScalar(0.55 + lum * 0.45 + n * 0.12);
        col.setXYZ(i, c.r, c.g, c.b);
      }
      col.needsUpdate = true;
    }
    const L = map.dungeon;
    if (!L) return;
    // The eyes: on the walls round the floor (a floor cell with a wall beside it), every few cells.
    const R = map.radius;
    const W = 2 * R + 1;
    const floor = new Set<number>();
    for (const h of L.floor) floor.add((h.r + R) * W + (h.q + R));
    const D: [number, number][] = [
      [1, 0],
      [1, -1],
      [0, -1],
      [-1, 0],
      [-1, 1],
      [0, 1],
    ];
    const kit = new Kit();
    const almond = new THREE.TorusGeometry(0.15, 0.03, 4, 14);
    const slab = new THREE.BoxGeometry(0.5, 0.36, 0.05);
    let k = 0;
    for (const h of L.floor) {
      for (const [dq, dr] of D) {
        const id = (h.r + dr + R) * W + (h.q + dq + R);
        if (floor.has(id)) continue;
        if (k++ % 3) continue;
        const a = hexCenter(h.q, h.r);
        const b = hexCenter(h.q + dq, h.r + dr);
        const mx = a.x + (b.x - a.x) * 0.47;
        const mz = a.z + (b.z - a.z) * 0.47;
        const yaw = Math.atan2(a.x - b.x, a.z - b.z);
        const p = this.ctx.at(mx / K, mz / K, 0.45);
        kit.add(slab, PALE[3] as string, place(p.x, p.y, p.z, yaw));
        kit.add(almond, '#2a2d33', place(p.x + Math.sin(yaw) * 0.03, p.y, p.z + Math.cos(yaw) * 0.03, yaw, new THREE.Vector3(1, 0.5, 1)));
        this.eyes.push({ p: new THREE.Vector3(p.x + Math.sin(yaw) * 0.08, p.y, p.z + Math.cos(yaw) * 0.08), x: mx / K, y: mz / K, lit: 0 });
        if (this.eyes.length >= 120) break;
      }
      if (this.eyes.length >= 120) break;
    }
    almond.dispose();
    slab.dispose();
    const mat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.9, metalness: 0.02, flatShading: true });
    this.mats.push(mat);
    const m = kit.build(mat, 'roots-eyes');
    if (m) {
      this.geos.push(m.geometry);
      this.group.add(m);
    }
    this.glow.mesh.frustumCulled = false;
    this.group.add(this.glow.mesh);
  }

  /** The eyes a hero (a paladin first of all) is near light, and stay lit. */
  tick(s: BattleState): void {
    const near2 = 2600 * 2600;
    for (const e of this.eyes) {
      if (e.lit > 0) continue;
      for (const h of s.heroes) {
        if (h.downTicks > 0 || h.pid.startsWith('npc:')) continue;
        const dx = h.x - e.x;
        const dy = h.y - e.y;
        if (dx * dx + dy * dy < near2) {
          e.lit = this.now || 0.001;
          this.ctx.vfx.burst(e.p.clone(), { n: 4, color: '#ff8a6e', speed: 0.02, up: 0.02, size: 0.04, life: 10 });
          break;
        }
      }
    }
  }

  frame(now: number): void {
    this.now = now;
    const g = this.glow;
    g.begin();
    for (let i = 0; i < this.eyes.length; i++) {
      const e = this.eyes[i] as { p: THREE.Vector3; x: number; y: number; lit: number };
      if (e.lit <= 0) continue;
      const t = now - e.lit;
      const k = Math.min(1, t / 0.6);
      // Lit with a flare, then steady, breathing slowly.
      const flare = t < 1.2 ? 1 + (1 - t / 1.2) * 1.5 : 1;
      const b = 0.85 + 0.15 * Math.sin(now * 1.3 + i);
      g.add(e.p.x, e.p.y, e.p.z, RED, 0.75 * flare, 0.5 * k * b);
      g.add(e.p.x, e.p.y, e.p.z, WHITE, 0.24, 0.75 * k);
    }
    g.end();
  }

  dispose(): void {
    for (const g of this.geos) g.dispose();
    for (const m of this.mats) m.dispose();
    this.glow.mesh.geometry.dispose();
    this.glow.material.dispose();
    this.group.removeFromParent();
  }
}

/** A hex's middle in world (x, z). */
const hexCenter = (q: number, r: number): { x: number; z: number } => ({ x: (q * 1000 + r * 500) * K, z: r * 866 * K });
